import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, LayersControl, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import JSZip from 'jszip';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { exerciseMapApi } from '../../lib/api';
import DOMPurify from 'dompurify';
import {
  Maximize2, Minimize2, Search, Crosshair,
  Ruler, Map as MapIcon, X, Check, Layers, MapPin, Download, Upload,
  FolderTree, Eye, EyeOff, ChevronDown, ChevronRight,
  FolderPlus, Pencil, Trash2, MoveRight, CheckCircle2, Globe,
} from 'lucide-react';

/* ── Fix CRA icon paths ─────────────────────────────────────────────────── */
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon   from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

/* ══════════════════════════════════════════════════════════════════════════
   CONSTANTS — Colors, Icons, Tile Layers
   ══════════════════════════════════════════════════════════════════════════ */

const PALETTE = [
  { name: 'Blue',   hex: '#3b82f6' },
  { name: 'Red',    hex: '#ef4444' },
  { name: 'Green',  hex: '#22c55e' },
  { name: 'Yellow', hex: '#eab308' },
  { name: 'Orange', hex: '#f97316' },
  { name: 'Purple', hex: '#a855f7' },
  { name: 'Cyan',   hex: '#06b6d4' },
  { name: 'White',  hex: '#f1f5f9' },
];

/* Google-Earth-style icon library — organised by category */
const ICON_CATEGORIES = {
  'General': {
    marker:     { label: 'Pin',        svg: c => `<circle cx="12" cy="14" r="7" fill="${c}" stroke="#fff" stroke-width="1.5"/><path d="M12 3v5" stroke="${c}" stroke-width="2.5"/>` },
    pushpin:    { label: 'Pushpin',    svg: c => `<circle cx="12" cy="8" r="6" fill="${c}" stroke="#fff" stroke-width="1.5"/><line x1="12" y1="14" x2="12" y2="23" stroke="${c}" stroke-width="2"/>` },
    flag:       { label: 'Flag',       svg: c => `<line x1="5" y1="2" x2="5" y2="22" stroke="${c}" stroke-width="2"/><path d="M5 2h14l-3 5 3 5H5z" fill="${c}" stroke="#fff" stroke-width="1"/>` },
    star:       { label: 'Star',       svg: c => `<polygon points="12,2 15,9 22,9 16.5,14 18.5,21 12,17 5.5,21 7.5,14 2,9 9,9" fill="${c}" stroke="#fff" stroke-width="1"/>` },
    diamond:    { label: 'Diamond',    svg: c => `<polygon points="12,2 22,12 12,22 2,12" fill="${c}" stroke="#fff" stroke-width="1.5"/>` },
    square:     { label: 'Square',     svg: c => `<rect x="3" y="3" width="18" height="18" rx="2" fill="${c}" stroke="#fff" stroke-width="1.5"/>` },
    circle:     { label: 'Circle',     svg: c => `<circle cx="12" cy="12" r="10" fill="${c}" stroke="#fff" stroke-width="1.5"/>` },
    triangle:   { label: 'Triangle',   svg: c => `<polygon points="12,2 22,22 2,22" fill="${c}" stroke="#fff" stroke-width="1.5"/>` },
    crosshair:  { label: 'Crosshair',  svg: c => `<circle cx="12" cy="12" r="8" fill="none" stroke="${c}" stroke-width="2"/><line x1="12" y1="2" x2="12" y2="6" stroke="${c}" stroke-width="2"/><line x1="12" y1="18" x2="12" y2="22" stroke="${c}" stroke-width="2"/><line x1="2" y1="12" x2="6" y2="12" stroke="${c}" stroke-width="2"/><line x1="18" y1="12" x2="22" y2="12" stroke="${c}" stroke-width="2"/>` },
  },
  'Emergency': {
    helipad:    { label: 'Helipad',    svg: c => `<rect x="2" y="2" width="20" height="20" rx="3" fill="${c}" stroke="#fff" stroke-width="1.5"/><text x="12" y="17" text-anchor="middle" font-size="14" font-weight="bold" fill="white">H</text>` },
    helicopter: { label: 'Helicopter', svg: c => `<circle cx="12" cy="12" r="10" fill="${c}" stroke="#fff" stroke-width="1.5"/><path d="M7 12h10M12 7v10M8 8l8 8M16 8l-8 8" stroke="white" stroke-width="1.2" fill="none"/>` },
    medical:    { label: 'Medical',    svg: c => `<circle cx="12" cy="12" r="10" fill="${c}" stroke="#fff" stroke-width="1.5"/><path d="M12 7v10M7 12h10" stroke="white" stroke-width="2.5"/>` },
    fire:       { label: 'Fire',       svg: c => `<path d="M12 2C8 8 4 11 4 15a8 8 0 0016 0c0-4-4-7-8-13z" fill="${c}" stroke="#fff" stroke-width="1.5"/>` },
    icp:        { label: 'ICP',        svg: c => `<rect x="2" y="4" width="20" height="16" rx="2" fill="${c}" stroke="#fff" stroke-width="1.5"/><text x="12" y="16" text-anchor="middle" font-size="10" font-weight="bold" fill="white">ICP</text>` },
    assembly:   { label: 'Assembly',   svg: c => `<polygon points="12,2 22,22 2,22" fill="${c}" stroke="#fff" stroke-width="1.5"/><text x="12" y="19" text-anchor="middle" font-size="10" font-weight="bold" fill="white">A</text>` },
    hazmat:     { label: 'Hazmat',     svg: c => `<polygon points="12,1 23,20 1,20" fill="${c}" stroke="#fff" stroke-width="1.5"/><text x="12" y="18" text-anchor="middle" font-size="12" font-weight="bold" fill="white">!</text>` },
    shelter:    { label: 'Shelter',    svg: c => `<path d="M2 12L12 3l10 9" fill="${c}" stroke="#fff" stroke-width="1.5"/><rect x="5" y="12" width="14" height="10" fill="${c}" stroke="#fff" stroke-width="1.5"/>` },
  },
  'Marine / Oil & Gas': {
    vessel:     { label: 'Vessel',     svg: c => `<path d="M4 16l2-6h12l2 6z" fill="${c}" stroke="#fff" stroke-width="1.5"/><path d="M8 10V6h8v4" fill="none" stroke="#fff" stroke-width="1.2"/><line x1="12" y1="6" x2="12" y2="3" stroke="#fff" stroke-width="1.5"/>` },
    oilrig:     { label: 'Oil Rig',    svg: c => `<rect x="7" y="10" width="10" height="12" fill="${c}" stroke="#fff" stroke-width="1.2"/><polygon points="8,10 12,2 16,10" fill="${c}" stroke="#fff" stroke-width="1.2"/><line x1="4" y1="22" x2="20" y2="22" stroke="#fff" stroke-width="2"/>` },
    anchor:     { label: 'Anchor',     svg: c => `<circle cx="12" cy="5" r="3" fill="none" stroke="${c}" stroke-width="2"/><line x1="12" y1="8" x2="12" y2="20" stroke="${c}" stroke-width="2"/><path d="M5 15c0 4 3.5 7 7 7s7-3 7-7" fill="none" stroke="${c}" stroke-width="2"/><line x1="8" y1="12" x2="16" y2="12" stroke="${c}" stroke-width="2"/>` },
    buoy:       { label: 'Buoy',       svg: c => `<circle cx="12" cy="9" r="7" fill="${c}" stroke="#fff" stroke-width="1.5"/><line x1="12" y1="16" x2="12" y2="22" stroke="${c}" stroke-width="2.5"/><line x1="8" y1="22" x2="16" y2="22" stroke="${c}" stroke-width="2"/>` },
    pipeline:   { label: 'Pipeline',   svg: c => `<rect x="2" y="8" width="20" height="8" rx="4" fill="${c}" stroke="#fff" stroke-width="1.5"/><circle cx="6" cy="12" r="2" fill="#fff"/><circle cx="18" cy="12" r="2" fill="#fff"/>` },
  },
  'Vehicles': {
    car:        { label: 'Car',        svg: c => `<rect x="3" y="10" width="18" height="8" rx="2" fill="${c}" stroke="#fff" stroke-width="1.2"/><path d="M6 10V7a1 1 0 011-1h10a1 1 0 011 1v3" fill="${c}" stroke="#fff" stroke-width="1.2"/><circle cx="7" cy="18" r="2" fill="#333" stroke="#fff" stroke-width="1"/><circle cx="17" cy="18" r="2" fill="#333" stroke="#fff" stroke-width="1"/>` },
    truck:      { label: 'Truck',      svg: c => `<rect x="1" y="8" width="14" height="10" rx="1" fill="${c}" stroke="#fff" stroke-width="1.2"/><rect x="15" y="12" width="8" height="6" rx="1" fill="${c}" stroke="#fff" stroke-width="1.2"/><circle cx="5" cy="18" r="2" fill="#333" stroke="#fff" stroke-width="1"/><circle cx="19" cy="18" r="2" fill="#333" stroke="#fff" stroke-width="1"/>` },
    boat:       { label: 'Boat',       svg: c => `<path d="M2 18l3-8h14l3 8z" fill="${c}" stroke="#fff" stroke-width="1.5"/><path d="M10 10V5h2v5" fill="none" stroke="#fff" stroke-width="1.5"/><path d="M12 5l6 3v2H12z" fill="#fff" opacity="0.5"/>` },
    plane:      { label: 'Aircraft',   svg: c => `<path d="M12 2l-2 8-7 2 2 1.5 5-1 -1 7h2V14l5 1 2-1.5-7-2z" fill="${c}" stroke="#fff" stroke-width="1"/>` },
  },
  'Facilities': {
    building:   { label: 'Building',   svg: c => `<rect x="4" y="6" width="16" height="16" rx="1" fill="${c}" stroke="#fff" stroke-width="1.2"/><rect x="8" y="10" width="3" height="3" fill="#fff" opacity="0.5"/><rect x="13" y="10" width="3" height="3" fill="#fff" opacity="0.5"/><rect x="10" y="17" width="4" height="5" fill="#fff" opacity="0.5"/>` },
    tower:      { label: 'Tower',      svg: c => `<polygon points="9,22 8,8 12,2 16,8 15,22" fill="${c}" stroke="#fff" stroke-width="1.5"/><line x1="9" y1="12" x2="15" y2="12" stroke="#fff" stroke-width="1"/><line x1="9" y1="17" x2="15" y2="17" stroke="#fff" stroke-width="1"/>` },
    warehouse:  { label: 'Warehouse',  svg: c => `<path d="M2 12L12 4l10 8" fill="${c}" stroke="#fff" stroke-width="1.5"/><rect x="4" y="12" width="16" height="10" fill="${c}" stroke="#fff" stroke-width="1.2"/><rect x="8" y="16" width="8" height="6" fill="#fff" opacity="0.3"/>` },
    gate:       { label: 'Gate',       svg: c => `<line x1="2" y1="22" x2="2" y2="6" stroke="${c}" stroke-width="3"/><line x1="22" y1="22" x2="22" y2="6" stroke="${c}" stroke-width="3"/><path d="M2 8h20" stroke="${c}" stroke-width="2"/><path d="M2 12h20" stroke="${c}" stroke-width="1.5" stroke-dasharray="3,2"/>` },
  },
};

/* Flat lookup for quick access by key */
const ALL_ICONS = {};
Object.values(ICON_CATEGORIES).forEach(cat => Object.assign(ALL_ICONS, cat));

function makeCustomIcon(iconType, color) {
  const ic = ALL_ICONS[iconType] || ALL_ICONS.marker;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24">${ic.svg(color)}</svg>`;
  return L.divIcon({
    className: '',
    html: `<div style="line-height:0;filter:drop-shadow(0 1px 3px rgba(0,0,0,0.4))">${svg}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
}

const TILE_LAYERS = [
  { name: 'Street (OSM)',       url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',                                                               attr: '© OpenStreetMap', checked: true },
  { name: 'Satellite (Esri)',   url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',                      attr: 'Esri World Imagery' },
  { name: 'Topographic',        url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',                                                                  attr: '© OpenTopoMap' },
  { name: 'Dark (CartoDB)',     url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',                                                     attr: '© CartoDB' },
  { name: 'Light (CartoDB)',    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',                                                    attr: '© CartoDB' },
  { name: 'Humanitarian',       url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',                                                             attr: '© OpenStreetMap Humanitarian' },
];

/* ══════════════════════════════════════════════════════════════════════════
   HELPERS
   ══════════════════════════════════════════════════════════════════════════ */

function polylineLength(layer) {
  const lls = layer.getLatLngs();
  const flat = Array.isArray(lls[0]) ? lls[0] : lls;
  let d = 0;
  for (let i = 0; i < flat.length - 1; i++) d += L.latLng(flat[i]).distanceTo(L.latLng(flat[i + 1]));
  return d;
}
function formatDist(m) { return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`; }
function polygonArea(layer) {
  try { return L.GeometryUtil.geodesicArea(layer.getLatLngs()[0]); } catch { return 0; }
}
function formatArea(m2) { return m2 >= 1e6 ? `${(m2 / 1e6).toFixed(2)} km²` : `${Math.round(m2)} m²`; }

function bearing(p1, p2) {
  const [la1, lo1, la2, lo2] = [p1.lat, p1.lng, p2.lat, p2.lng].map(d => d * Math.PI / 180);
  const y = Math.sin(lo2 - lo1) * Math.cos(la2);
  const x = Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(lo2 - lo1);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

function arrowIcon(color, deg) {
  return L.divIcon({
    className: '',
    html: `<div style="transform:rotate(${deg}deg);line-height:0;display:inline-block">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="-10 -10 20 20">
        <polygon points="0,-9 7,5 0,2 -7,5" fill="${color}" stroke="rgba(255,255,255,0.6)" stroke-width="0.8"/>
      </svg></div>`,
    iconSize: [20, 20], iconAnchor: [10, 10],
  });
}

function _genFeatureId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `f_${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`;
  }
  return `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function toCleanGeoJSON(drawnItems) {
  const gj = drawnItems.toGeoJSON();
  gj.features = gj.features.filter(f => !f.properties?.isArrowhead);
  // V14.5: ensure every feature has a stable featureId so the server can merge
  // concurrent saves from multiple admins without losing data.
  gj.features.forEach((f) => {
    if (!f.properties) f.properties = {};
    if (!f.properties.featureId && !f.properties.id) {
      f.properties.featureId = _genFeatureId();
    } else if (!f.properties.featureId) {
      f.properties.featureId = f.properties.id;
    }
  });
  return gj;
}

/* ── KML/KMZ parsing helpers ──────────────────────────────────────────── */

/** Get text content of the first descendant with given tag (namespace-safe) */
function xmlText(node, tag) {
  const el = node.getElementsByTagName(tag)[0];
  return el ? el.textContent.trim() : '';
}

/** Get text content of a DIRECT child element (does not recurse) */
function directChildText(node, tag) {
  if (!node || !node.children) return '';
  for (let i = 0; i < node.children.length; i++) {
    const c = node.children[i];
    if (c.tagName === tag || c.localName === tag) return c.textContent.trim();
  }
  return '';
}

/** Find the first DIRECT child element with given tag */
function directChild(node, tag) {
  if (!node || !node.children) return null;
  for (let i = 0; i < node.children.length; i++) {
    const c = node.children[i];
    if (c.tagName === tag || c.localName === tag) return c;
  }
  return null;
}

/** Parse coordinates string "lng,lat,alt lng,lat,alt ..." into [[lng,lat], ...] */
function parseCoordStr(str) {
  return str.trim().split(/\s+/).filter(Boolean).map(triplet => {
    const parts = triplet.split(',').map(Number);
    return [parts[0], parts[1]]; // [lng, lat]
  }).filter(c => !isNaN(c[0]) && !isNaN(c[1]));
}

/** Extract folder name for a placemark (walk up to nearest Folder parent) */
function getFolder(pm) {
  let n = pm.parentElement;
  while (n) {
    if (n.tagName === 'Folder') {
      const name = directChildText(n, 'name');
      if (name) return name;
    }
    if (n.tagName === 'Document') break;
    n = n.parentElement;
  }
  return 'Imported';
}

/** Convert KML AABBGGRR hex string -> { hex: '#rrggbb', opacity: 0..1 } */
function kmlColorToHex(aabbggrr, fallback = '#3b82f6') {
  if (!aabbggrr || typeof aabbggrr !== 'string') return { hex: fallback, opacity: 1 };
  const s = aabbggrr.trim().toLowerCase().replace(/[^0-9a-f]/g, '');
  if (s.length !== 8) return { hex: fallback, opacity: 1 };
  const a = parseInt(s.slice(0, 2), 16);
  const b = s.slice(2, 4);
  const g = s.slice(4, 6);
  const r = s.slice(6, 8);
  return { hex: `#${r}${g}${b}`, opacity: isNaN(a) ? 1 : a / 255 };
}

/** Rewrite relative image src/href in HTML description text using the KMZ imageMap */
function rewriteDescriptionImages(html, imageMap) {
  if (!html || !imageMap) return html || '';
  // Replace src="..." and href="..." that point to known image files in the KMZ.
  return html.replace(/(src|href)\s*=\s*"([^"]+)"/gi, (m, attr, url) => {
    // Skip absolute URLs and data URLs
    if (/^(data:|https?:|mailto:|tel:|#)/i.test(url)) return m;
    const file = url.split('/').pop().split('?')[0];
    const dataUrl = imageMap.get(url) || imageMap.get(file) || imageMap.get(url.replace(/^\.?\//, '').replace(/^files\//, ''));
    return dataUrl ? `${attr}="${dataUrl}"` : m;
  });
}

/** Resolve an icon href against the KMZ image map (returns data URL or original href) */
function resolveIconHref(href, imageMap) {
  if (!href) return '';
  // Upgrade HTTP -> HTTPS to avoid mixed-content blocking on HTTPS pages.
  // Google's KML icon servers (maps.google.com/mapfiles/...) serve over both.
  let normalized = href.replace(/^http:\/\//i, 'https://');
  if (!imageMap) return normalized;
  // Try exact, then filename-only
  const direct = imageMap.get(href) || imageMap.get(normalized);
  if (direct) return direct;
  const file = href.split('/').pop().split('?')[0];
  const byFile = imageMap.get(file);
  if (byFile) return byFile;
  // Strip leading "files/" or "./"
  const stripped = href.replace(/^\.?\//, '').replace(/^files\//, '');
  return imageMap.get(stripped) || normalized;
}

/** Parse a single <Style> node into a flat style object */
function parseStyleNode(styleNode, imageMap) {
  if (!styleNode) return {};
  const style = {};

  // IconStyle
  const iconStyle = directChild(styleNode, 'IconStyle');
  if (iconStyle) {
    const iconEl = directChild(iconStyle, 'Icon');
    const href = iconEl ? directChildText(iconEl, 'href') : '';
    if (href) style.iconUrl = resolveIconHref(href, imageMap);
    const scale = parseFloat(directChildText(iconStyle, 'scale'));
    if (!isNaN(scale) && scale > 0) style.iconScale = scale;
    const iconColor = directChildText(iconStyle, 'color');
    if (iconColor) {
      const c = kmlColorToHex(iconColor);
      style.iconColor = c.hex;
      style.iconOpacity = c.opacity;
    }
    const heading = parseFloat(directChildText(iconStyle, 'heading'));
    if (!isNaN(heading)) style.iconHeading = heading;
    // hotSpot: x/y/xunits/yunits
    const hotSpot = directChild(iconStyle, 'hotSpot');
    if (hotSpot) {
      const x = parseFloat(hotSpot.getAttribute('x'));
      const y = parseFloat(hotSpot.getAttribute('y'));
      const xu = hotSpot.getAttribute('xunits') || 'fraction';
      const yu = hotSpot.getAttribute('yunits') || 'fraction';
      if (!isNaN(x) && !isNaN(y)) style.iconHotspot = { x, y, xu, yu };
    }
  }

  // LineStyle (stroke for lines & polygon outlines)
  const lineStyle = directChild(styleNode, 'LineStyle');
  if (lineStyle) {
    const lc = directChildText(lineStyle, 'color');
    if (lc) {
      const c = kmlColorToHex(lc);
      style.strokeColor = c.hex;
      style.strokeOpacity = c.opacity;
    }
    const w = parseFloat(directChildText(lineStyle, 'width'));
    if (!isNaN(w) && w > 0) style.strokeWidth = w;
  }

  // PolyStyle (polygon fill + outline toggle)
  const polyStyle = directChild(styleNode, 'PolyStyle');
  if (polyStyle) {
    const pc = directChildText(polyStyle, 'color');
    if (pc) {
      const c = kmlColorToHex(pc);
      style.fillColor = c.hex;
      style.fillOpacity = c.opacity;
    }
    const fill = directChildText(polyStyle, 'fill');
    if (fill === '0') style.fillOpacity = 0;
    const outline = directChildText(polyStyle, 'outline');
    if (outline === '0') style.noOutline = true;
  }

  // LabelStyle
  const labelStyle = directChild(styleNode, 'LabelStyle');
  if (labelStyle) {
    const lc = directChildText(labelStyle, 'color');
    if (lc) {
      const c = kmlColorToHex(lc);
      style.labelColor = c.hex;
    }
    const ls = parseFloat(directChildText(labelStyle, 'scale'));
    if (!isNaN(ls)) style.labelScale = ls;
  }

  return style;
}

/** Build a Map<styleId, styleObj> from all <Style> + <StyleMap> in the document. */
function parseKMLStyles(doc, imageMap) {
  const styles = new Map();

  // Pass 1: all <Style id="..."> nodes (anywhere in the doc)
  const styleEls = doc.getElementsByTagName('Style');
  for (let i = 0; i < styleEls.length; i++) {
    const el = styleEls[i];
    const id = el.getAttribute('id');
    if (!id) continue;
    styles.set(id, parseStyleNode(el, imageMap));
  }

  // Pass 2: <StyleMap id="..."> with normal/highlight pairs — use 'normal'
  const styleMapEls = doc.getElementsByTagName('StyleMap');
  for (let i = 0; i < styleMapEls.length; i++) {
    const el = styleMapEls[i];
    const id = el.getAttribute('id');
    if (!id) continue;
    const pairs = el.getElementsByTagName('Pair');
    let normalRef = null;
    let firstRef = null;
    for (let j = 0; j < pairs.length; j++) {
      const key = directChildText(pairs[j], 'key');
      const url = directChildText(pairs[j], 'styleUrl');
      const inlineStyle = directChild(pairs[j], 'Style');
      const ref = url ? url.replace(/^#/, '') : null;
      if (!firstRef && (ref || inlineStyle)) {
        firstRef = ref ? styles.get(ref) : (inlineStyle ? parseStyleNode(inlineStyle, imageMap) : null);
      }
      if (key === 'normal') {
        normalRef = ref ? styles.get(ref) : (inlineStyle ? parseStyleNode(inlineStyle, imageMap) : null);
        break;
      }
    }
    styles.set(id, normalRef || firstRef || {});
  }

  return styles;
}

/** Resolve the effective style for a Placemark — merges styleUrl reference + inline <Style>. */
function resolveKMLStyle(pm, stylesMap, imageMap) {
  let merged = {};
  // styleUrl reference
  const styleUrl = directChildText(pm, 'styleUrl');
  if (styleUrl) {
    const id = styleUrl.replace(/^#/, '');
    const ref = stylesMap.get(id);
    if (ref) merged = { ...merged, ...ref };
  }
  // Inline <Style> on the placemark
  const inline = directChild(pm, 'Style');
  if (inline) merged = { ...merged, ...parseStyleNode(inline, imageMap) };
  // Inline <StyleMap> on the placemark (rare)
  const inlineMap = directChild(pm, 'StyleMap');
  if (inlineMap) {
    const pairs = inlineMap.getElementsByTagName('Pair');
    for (let j = 0; j < pairs.length; j++) {
      if (directChildText(pairs[j], 'key') === 'normal') {
        const url = directChildText(pairs[j], 'styleUrl');
        if (url) {
          const ref = stylesMap.get(url.replace(/^#/, ''));
          if (ref) merged = { ...merged, ...ref };
        }
        const ist = directChild(pairs[j], 'Style');
        if (ist) merged = { ...merged, ...parseStyleNode(ist, imageMap) };
        break;
      }
    }
  }
  return merged;
}

/** Parse a KML XML document into an array of GeoJSON features.
 *  imageMap: optional Map of filename -> data URL for KMZ embedded images */
function parseKMLDocument(doc, imageMap) {
  const features = [];
  const stylesMap = parseKMLStyles(doc, imageMap);
  const placemarks = doc.getElementsByTagName('Placemark');
  for (let i = 0; i < placemarks.length; i++) {
    const pm = placemarks[i];
    const name = directChildText(pm, 'name');
    const desc = rewriteDescriptionImages(directChildText(pm, 'description'), imageMap);
    const folder = getFolder(pm);
    const kmlStyle = resolveKMLStyle(pm, stylesMap, imageMap);

    // Determine primary geometry-relevant color for legacy "color" field
    // (used by older draw flows). Prefer fill for polygons, stroke otherwise.
    const primaryColor = kmlStyle.fillColor || kmlStyle.strokeColor || kmlStyle.iconColor || '#3b82f6';

    // Try all geometry types
    const pointEls = pm.getElementsByTagName('Point');
    const lineEls = pm.getElementsByTagName('LineString');
    const polyEls = pm.getElementsByTagName('Polygon');
    const multiEls = pm.getElementsByTagName('MultiGeometry');

    const base = {
      label: name,
      description: desc,
      color: primaryColor,
      layer: folder,
      kmlStyle, // full style object for renderer
    };

    if (pointEls.length > 0) {
      const coordStr = xmlText(pointEls[0], 'coordinates');
      const coords = parseCoordStr(coordStr);
      if (coords.length > 0) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: coords[0] },
          properties: { ...base, iconType: kmlStyle.iconUrl ? 'kmlIcon' : 'pushpin' },
        });
      }
    } else if (lineEls.length > 0) {
      const coordStr = xmlText(lineEls[0], 'coordinates');
      const coords = parseCoordStr(coordStr);
      if (coords.length >= 2) {
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: coords },
          properties: { ...base },
        });
      }
    } else if (polyEls.length > 0) {
      const outerRing = polyEls[0].getElementsByTagName('outerBoundaryIs')[0];
      if (outerRing) {
        const coordStr = xmlText(outerRing, 'coordinates');
        const coords = parseCoordStr(coordStr);
        if (coords.length >= 3) {
          features.push({
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [coords] },
            properties: { ...base },
          });
        }
      }
    } else if (multiEls.length > 0) {
      // Handle MultiGeometry by recursing into child geometries (all share style)
      const mg = multiEls[0];
      const pts = mg.getElementsByTagName('Point');
      for (let j = 0; j < pts.length; j++) {
        const cs = parseCoordStr(xmlText(pts[j], 'coordinates'));
        if (cs.length) features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: cs[0] }, properties: { ...base, iconType: kmlStyle.iconUrl ? 'kmlIcon' : 'pushpin' } });
      }
      const lines = mg.getElementsByTagName('LineString');
      for (let j = 0; j < lines.length; j++) {
        const cs = parseCoordStr(xmlText(lines[j], 'coordinates'));
        if (cs.length >= 2) features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: cs }, properties: { ...base } });
      }
      const polys = mg.getElementsByTagName('Polygon');
      for (let j = 0; j < polys.length; j++) {
        const ob = polys[j].getElementsByTagName('outerBoundaryIs')[0];
        if (ob) {
          const cs = parseCoordStr(xmlText(ob, 'coordinates'));
          if (cs.length >= 3) features.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [cs] }, properties: { ...base } });
        }
      }
    }
  }

  // Parse GroundOverlay elements (image overlays on the map)
  const groundOverlays = doc.getElementsByTagName('GroundOverlay');
  for (let i = 0; i < groundOverlays.length; i++) {
    const go = groundOverlays[i];
    const name = xmlText(go, 'name');
    const folder = getFolder(go);

    // Get image href
    const iconEl = go.getElementsByTagName('Icon')[0];
    let href = iconEl ? xmlText(iconEl, 'href') : '';
    if (!href) continue;

    // Resolve image from KMZ imageMap if available
    let imageUrl = href;
    if (imageMap) {
      // Try exact match first, then filename only
      const resolved = imageMap.get(href) || imageMap.get(href.split('/').pop());
      if (resolved) imageUrl = resolved;
    }

    // Get bounds from LatLonBox
    const llBox = go.getElementsByTagName('LatLonBox')[0];
    if (!llBox) continue;
    const north = parseFloat(xmlText(llBox, 'north'));
    const south = parseFloat(xmlText(llBox, 'south'));
    const east = parseFloat(xmlText(llBox, 'east'));
    const west = parseFloat(xmlText(llBox, 'west'));
    const rotation = parseFloat(xmlText(llBox, 'rotation')) || 0;

    if (isNaN(north) || isNaN(south) || isNaN(east) || isNaN(west)) continue;

    features.push({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [[[west, south], [east, south], [east, north], [west, north], [west, south]]],
      },
      properties: {
        label: name || 'Ground Overlay',
        layer: folder,
        isGroundOverlay: true,
        imageUrl,
        bounds: [[south, west], [north, east]],
        rotation,
        color: 'transparent',
      },
    });
  }

  return features;
}

/* ══════════════════════════════════════════════════════════════════════════
   SUB-COMPONENTS
   ══════════════════════════════════════════════════════════════════════════ */

function MapCapture({ mapRef }) {
  const map = useMap();
  useEffect(() => { mapRef.current = map; }, [map]);
  return null;
}

function CoordTracker({ onCoords }) {
  useMapEvents({
    mousemove: e => onCoords(e.latlng),
    mouseout:  () => onCoords(null),
  });
  return null;
}

function ScaleBar() {
  const map = useMap();
  useEffect(() => {
    const s = L.control.scale({ position: 'bottomright', imperial: true, metric: true });
    map.addControl(s);
    return () => map.removeControl(s);
  }, [map]);
  return null;
}

function LocateMe() {
  const map = useMap();
  useEffect(() => {
    const Ctrl = L.Control.extend({
      options: { position: 'topleft' },
      onAdd() {
        const btn = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        btn.innerHTML = `<a title="Locate me" style="display:flex;align-items:center;justify-content:center;font-size:15px;width:30px;height:30px;cursor:pointer;">&#8853;</a>`;
        L.DomEvent.on(btn, 'click', e => { L.DomEvent.preventDefault(e); map.locate({ setView: true, maxZoom: 14 }); });
        return btn;
      },
    });
    const ctrl = new Ctrl();
    map.addControl(ctrl);
    map.on('locationfound', e => {
      L.circleMarker(e.latlng, { radius: 8, color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.5 })
        .addTo(map).bindPopup('You are here').openPopup();
    });
    map.on('locationerror', () => toast.error('Location not available'));
    return () => map.removeControl(ctrl);
  }, [map]);
  return null;
}

function MiniMap({ visible }) {
  const map = useMap();
  const ctrlRef = useRef(null);
  const miniRef = useRef(null);
  useEffect(() => {
    if (!map || !visible) return;
    const Ctrl = L.Control.extend({
      options: { position: 'bottomleft' },
      onAdd() {
        const div = L.DomUtil.create('div');
        Object.assign(div.style, { width: '160px', height: '120px', border: '2px solid rgba(255,255,255,0.35)', borderRadius: '6px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' });
        setTimeout(() => { miniRef.current = L.map(div, { zoomControl: false, attributionControl: false, dragging: false, scrollWheelZoom: false }).setView(map.getCenter(), Math.max(map.getZoom() - 4, 1)); L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png').addTo(miniRef.current); }, 0);
        return div;
      },
    });
    ctrlRef.current = new Ctrl();
    map.addControl(ctrlRef.current);
    const sync = () => miniRef.current?.setView(map.getCenter(), Math.max(map.getZoom() - 4, 1));
    map.on('move', sync);
    return () => {
      map.off('move', sync);
      if (ctrlRef.current) map.removeControl(ctrlRef.current);
      if (miniRef.current) { miniRef.current.remove(); miniRef.current = null; }
    };
  }, [map, visible]);
  return null;
}

function RightClickCoords() {
  const map = useMap();
  useEffect(() => {
    const handler = e => {
      const { lat, lng } = e.latlng;
      L.popup().setLatLng(e.latlng).setContent(`<div style="font-size:11px;font-family:monospace">${lat.toFixed(6)}, ${lng.toFixed(6)}<br><button onclick="navigator.clipboard.writeText('${lat.toFixed(6)}, ${lng.toFixed(6)}');this.textContent='Copied!'" style="margin-top:4px;cursor:pointer;color:#3b82f6;border:none;background:none;padding:0;font-size:11px">Copy</button></div>`).openOn(map);
    };
    map.on('contextmenu', handler);
    return () => map.off('contextmenu', handler);
  }, [map]);
  return null;
}

/* ── BriefingMode overlay (V14.6) ───────────────────────────────────────
   Adds Esri World Imagery satellite tiles ON TOP of the active basemap when
   briefingMode is on. Sits below labels but above whatever street/dark/light
   tiles the user has selected via LayersControl.                           */
function BriefingModeOverlay({ active }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !active) return;
    const sat = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      { attribution: 'Esri World Imagery', zIndex: 250, maxZoom: 19 }
    );
    sat.addTo(map);
    return () => { try { map.removeLayer(sat); } catch { /* no-op */ } };
  }, [map, active]);
  return null;
}

/* ── DrawControl ────────────────────────────────────────────────────────── */

function DrawControl({ canEdit, geojson, colorRef, arrowModeRef, iconRef, onCreated, onChanged, onFeatureClick, onFeatureDeleted, hiddenLayers, hiddenFeatures, briefingMode }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    /* ── Two groups: editable vs display-only ── */
    const drawnItems = new L.FeatureGroup();   // Visible features → Leaflet.Draw scope
    const hiddenItems = new L.FeatureGroup();   // Hidden features → display only, NO edit handles
    map.addLayer(drawnItems);
    map.addLayer(hiddenItems);

    /* Track which original features are hidden so we can merge them back on save */
    const hiddenOriginalFeatures = [];

    const cleanFeatures = (geojson?.features || []).filter(f => !f.properties?.isArrowhead);
    const overlayLayers = []; // Track image overlay layers for cleanup
    if (cleanFeatures.length) {
      try {
        // Separate ground overlays from regular features
        const regularFeatures = [];
        const groundOverlays = [];
        cleanFeatures.forEach((f, i) => {
          if (f.properties?.isGroundOverlay) {
            groundOverlays.push({ ...f, properties: { ...f.properties, _cleanIdx: i } });
          } else {
            regularFeatures.push({ ...f, properties: { ...f.properties, _cleanIdx: i } });
          }
        });

        // Render ground overlays as Leaflet image overlays
        groundOverlays.forEach(f => {
          const hidden = hiddenLayers?.has(f.properties?.layer) || hiddenFeatures?.has(f.properties?._cleanIdx);
          if (hidden) return;
          const { imageUrl, bounds, rotation } = f.properties;
          if (!imageUrl || !bounds) return;
          try {
            const overlay = L.imageOverlay(imageUrl, bounds, {
              opacity: 0.85,
              interactive: true,
            });
            overlay.addTo(map);
            overlayLayers.push(overlay);
            if (f.properties.label) {
              const popupHtml = `<b>${f.properties.label}</b>${f.properties.description ? '<br>' + f.properties.description : ''}`;
              overlay.bindPopup(DOMPurify.sanitize(popupHtml));
            }
          } catch (overlayErr) {
            console.warn('Failed to create image overlay:', overlayErr);
          }
        });

        // Split regular features: visible → drawnItems, hidden → hiddenItems (no edit handles)
        regularFeatures.forEach(f => {
          const isHidden = hiddenLayers?.has(f.properties?.layer) || hiddenFeatures?.has(f.properties?._cleanIdx);
          if (isHidden) {
            // Track original feature for merge-back on save
            hiddenOriginalFeatures.push(f);
            return; // Don't add to ANY Leaflet group — skip completely
          }
        });

        // Add ONLY visible features to drawnItems (Leaflet.Draw scope)
        const visibleFeatures = regularFeatures.filter(f => {
          const isHidden = hiddenLayers?.has(f.properties?.layer) || hiddenFeatures?.has(f.properties?._cleanIdx);
          return !isHidden;
        });

        L.geoJSON({ type: 'FeatureCollection', features: visibleFeatures }, {
          style: f => {
            const k = f.properties?.kmlStyle || {};
            const baseColor = f.properties?.color || '#3b82f6';
            // Polygon: use PolyStyle fill + LineStyle stroke; Line: use LineStyle stroke
            const strokeColor = k.strokeColor || baseColor;
            const strokeOpacity = (typeof k.strokeOpacity === 'number') ? k.strokeOpacity : 1;
            const strokeWidth = k.strokeWidth || 2;
            const fillColor = k.fillColor || baseColor;
            let fillOpacity = (typeof k.fillOpacity === 'number') ? k.fillOpacity : 0.15;
            let weight = k.noOutline ? 0 : strokeWidth;
            // V14.6 — Earth/briefing mode: bump fills + outlines for high-contrast briefing-deck look
            if (briefingMode) {
              fillOpacity = Math.min(0.65, fillOpacity * 2 + 0.1);
              weight = k.noOutline ? 0 : Math.max(weight, 2.5);
            }
            return {
              color: strokeColor,
              fillColor,
              weight,
              opacity: k.noOutline ? 0 : strokeOpacity,
              fillOpacity,
            };
          },
          pointToLayer: (f, latlng) => {
            const k = f.properties?.kmlStyle || {};
            if (f.properties?.radius)
              return L.circle(latlng, { radius: f.properties.radius, color: f.properties.color || '#3b82f6', fillOpacity: 0.15 });
            // KML custom icon (from KMZ resources or external URL)
            if (k.iconUrl) {
              const scale = k.iconScale || 1;
              const size = Math.max(16, Math.round(32 * scale));
              const hs = k.iconHotspot;
              let anchor = [size / 2, size / 2];
              if (hs && hs.xu === 'fraction' && hs.yu === 'fraction') {
                // KML y=0 is bottom; Leaflet y=0 is top
                anchor = [hs.x * size, (1 - hs.y) * size];
              }
              return L.marker(latlng, {
                icon: L.icon({
                  iconUrl: k.iconUrl,
                  iconSize: [size, size],
                  iconAnchor: anchor,
                  className: 'kml-imported-icon',
                }),
              });
            }
            const iconType = f.properties?.iconType || 'marker';
            return L.marker(latlng, { icon: makeCustomIcon(iconType, f.properties?.color || '#3b82f6') });
          },
          onEachFeature: (f, layer) => {
            // Hover-tooltip only (NOT permanent) — prevents the label-stack clutter
            // that happens when 400+ KML placemarks all show their names at once,
            // including in briefing mode where dense polygons stack their labels.
            if (f.properties?.label) {
              layer.bindTooltip(f.properties.label, {
                direction: 'top',
                sticky: true,
                className: 'map-label-tip',
              });
            }
            const popupParts = [];
            if (f.properties?.label) popupParts.push(`<b>${f.properties.label}</b>`);
            if (f.properties?.iconType && ALL_ICONS[f.properties.iconType]) popupParts.push(`<em style="font-size:11px;">${ALL_ICONS[f.properties.iconType].label}</em>`);
            if (f.properties?.description) {
              // KML descriptions can contain HTML (img tags etc.). Wrap in a sized
              // container so embedded `style="max-width:500px"` on <img> doesn't blow
              // the popup outside the map. CSS clamps width/height to popup bounds.
              popupParts.push(
                `<div class="kml-desc">${f.properties.description}</div>`
              );
            }
            if (popupParts.length) layer.bindPopup(DOMPurify.sanitize(popupParts.join('<br>')), { maxWidth: 340, maxHeight: 420 });
            layer.on('click', () => { if (onFeatureClick) onFeatureClick(f.properties); });
            if (f.properties?.type === 'arrow' && f.geometry?.type === 'LineString') {
              const coords = f.geometry.coordinates;
              if (coords.length >= 2) {
                const p1 = L.latLng(coords[coords.length - 2][1], coords[coords.length - 2][0]);
                const p2 = L.latLng(coords[coords.length - 1][1], coords[coords.length - 1][0]);
                const tip = L.marker(p2, { icon: arrowIcon(f.properties.color || '#3b82f6', bearing(p1, p2)), interactive: false });
                tip.feature = { type: 'Feature', properties: { isArrowhead: true }, geometry: null };
                drawnItems.addLayer(tip);
              }
            }
          },
        }).eachLayer(l => drawnItems.addLayer(l));
      } catch (e) { console.warn('GeoJSON load:', e); }
    }

    let drawCtrl = null;
    if (canEdit) {
      drawCtrl = new L.Control.Draw({
        position: 'topright',
        edit: { featureGroup: drawnItems, remove: true },
        draw: {
          polyline: { shapeOptions: { color: colorRef.current, weight: 3 } },
          polygon:  { shapeOptions: { color: colorRef.current }, showArea: true },
          rectangle: { shapeOptions: { color: colorRef.current } },
          circle:   { shapeOptions: { color: colorRef.current } },
          marker:   true,
          circlemarker: false,
        },
      });
      map.addControl(drawCtrl);
    }

    const handleCreated = e => {
      const { layer, layerType } = e;
      const color = colorRef.current;
      if (layer.setStyle) layer.setStyle({ color, fillColor: color, fillOpacity: 0.15 });
      layer.feature = layer.feature || { type: 'Feature', geometry: null, properties: {} };
      layer.feature.properties.color = color;
      if (layerType === 'circle') layer.feature.properties.radius = layer.getRadius();
      if (layerType === 'marker') {
        const iconType = iconRef.current || 'marker';
        layer.feature.properties.iconType = iconType;
        layer.setIcon(makeCustomIcon(iconType, color));
      }
      if (arrowModeRef.current && layerType === 'polyline') {
        layer.feature.properties.type = 'arrow';
        const lls = layer.getLatLngs();
        if (lls.length >= 2) {
          const tip = L.marker(lls[lls.length - 1], { icon: arrowIcon(color, bearing(lls[lls.length - 2], lls[lls.length - 1])), interactive: false });
          tip.feature = { type: 'Feature', properties: { isArrowhead: true }, geometry: null };
          drawnItems.addLayer(tip);
        }
      }

      let meas = null;
      if (layerType === 'polyline') meas = `Length: ${formatDist(polylineLength(layer))}`;
      else if (layerType === 'polygon' || layerType === 'rectangle') meas = `Area: ${formatArea(polygonArea(layer))}`;
      else if (layerType === 'circle') meas = `Radius: ${formatDist(layer.getRadius())}`;
      onCreated(layer, drawnItems, meas, hiddenOriginalFeatures);
    };

    const handleEdited = () => onChanged(drawnItems, hiddenOriginalFeatures);
    const handleDeleted = (e) => {
      // V14.5: capture deleted featureIds so the server can apply the delete
      // without us having to send the full geojson (which would lose other users' work)
      try {
        e?.layers?.eachLayer?.((layer) => {
          const fid = layer?.feature?.properties?.featureId || layer?.feature?.properties?.id;
          if (fid && typeof onFeatureDeleted === 'function') onFeatureDeleted(fid);
        });
      } catch (_) { /* no-op */ }
      onChanged(drawnItems, hiddenOriginalFeatures);
    };

    map.on(L.Draw.Event.CREATED, handleCreated);
    map.on(L.Draw.Event.EDITED, handleEdited);
    map.on(L.Draw.Event.DELETED, handleDeleted);

    return () => {
      map.off(L.Draw.Event.CREATED, handleCreated);
      map.off(L.Draw.Event.EDITED, handleEdited);
      map.off(L.Draw.Event.DELETED, handleDeleted);
      if (drawCtrl) map.removeControl(drawCtrl);
      map.removeLayer(drawnItems);
      map.removeLayer(hiddenItems);
      // Clean up image overlays
      overlayLayers.forEach(ol => { try { map.removeLayer(ol); } catch {} });
    };
  }, [map, canEdit, geojson, colorRef, arrowModeRef, iconRef, onCreated, onChanged, onFeatureClick, onFeatureDeleted, hiddenLayers, hiddenFeatures, briefingMode]);

  return null;
}

/* ══════════════════════════════════════════════════════════════════════════
   LAYER TREE — Advanced folder management (like Google Earth)
   ══════════════════════════════════════════════════════════════════════════ */

function LayerTree({ features, folders, setFolders, hiddenLayers, setHiddenLayers, hiddenFeatures, setHiddenFeatures, onPersistFolders, onMoveFeature, canEdit }) {
  const [expandedCats, setExpandedCats] = useState({});
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParent, setNewFolderParent] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [movingFeatureIdx, setMovingFeatureIdx] = useState(null);

  /* Group features into layers by their `layer` property */
  const layerGroups = useMemo(() => {
    const groups = {};
    (features || []).filter(f => !f.properties?.isArrowhead).forEach((f, idx) => {
      const layer = f.properties?.layer || 'Unsorted';
      if (!groups[layer]) groups[layer] = { points: 0, lines: 0, polygons: 0, total: 0, features: [] };
      groups[layer].total++;
      groups[layer].features.push({ ...f, _idx: idx });
      if (f.geometry?.type === 'Point') groups[layer].points++;
      else if (f.geometry?.type === 'LineString') groups[layer].lines++;
      else groups[layer].polygons++;
    });
    return groups;
  }, [features]);

  /* Build tree from folders + auto-discovered layers */
  const folderTree = useMemo(() => {
    const tree = [];
    const folderMap = {};
    (folders || []).forEach(f => { folderMap[f.id] = { ...f, children: [], featureGroup: layerGroups[f.name] || null }; });
    (folders || []).forEach(f => {
      if (f.parent_id && folderMap[f.parent_id]) folderMap[f.parent_id].children.push(folderMap[f.id]);
      else tree.push(folderMap[f.id]);
    });
    // Add auto-discovered layers not in any folder
    const folderNames = new Set((folders || []).map(f => f.name));
    Object.keys(layerGroups).forEach(name => {
      if (!folderNames.has(name)) tree.push({ id: `auto_${name}`, name, auto: true, children: [], featureGroup: layerGroups[name] });
    });
    return tree;
  }, [folders, layerGroups]);

  const toggleLayer = (name) => {
    setHiddenLayers(prev => { const n = new Set(prev); if (n.has(name)) n.delete(name); else n.add(name); return n; });
  };
  const toggleFeature = (idx) => {
    setHiddenFeatures(prev => { const n = new Set(prev); if (n.has(idx)) n.delete(idx); else n.add(idx); return n; });
  };
  const toggleCat = (id) => setExpandedCats(prev => ({ ...prev, [id]: !prev[id] }));

  const allLayerNames = useMemo(() => Object.keys(layerGroups), [layerGroups]);
  const allVisible = allLayerNames.every(n => !hiddenLayers.has(n));
  const toggleAll = () => {
    if (allVisible) setHiddenLayers(new Set(allLayerNames));
    else setHiddenLayers(new Set());
  };

  const createFolder = () => {
    if (!newFolderName.trim()) return;
    const id = 'folder_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const newFolder = { id, name: newFolderName.trim(), parent_id: newFolderParent || null, color: '#3b82f6' };
    const updated = [...(folders || []), newFolder];
    setFolders(updated);
    onPersistFolders(updated);
    setNewFolderName('');
    setShowNewFolder(false);
    setNewFolderParent(null);
    toast.success(`Folder "${newFolder.name}" created`);
  };

  const renameFolder = (id) => {
    if (!renameVal.trim()) return;
    const oldName = (folders || []).find(f => f.id === id)?.name;
    const updated = (folders || []).map(f => f.id === id ? { ...f, name: renameVal.trim() } : f);
    setFolders(updated);
    onPersistFolders(updated);
    // Also rename all features in that layer
    if (oldName && oldName !== renameVal.trim()) onMoveFeature(oldName, renameVal.trim());
    setRenamingId(null);
    setRenameVal('');
  };

  const deleteFolder = (id) => {
    const folder = (folders || []).find(f => f.id === id);
    if (!folder) return;
    if (!window.confirm(`Delete folder "${folder.name}"? Features will move to "Unsorted".`)) return;
    const updated = (folders || []).filter(f => f.id !== id && f.parent_id !== id);
    setFolders(updated);
    onPersistFolders(updated);
    onMoveFeature(folder.name, 'Unsorted');
  };

  const moveFeatureToFolder = (featureIdx, targetLayer) => {
    onMoveFeature(null, targetLayer, featureIdx);
    setMovingFeatureIdx(null);
    toast.success('Feature moved');
  };

  const allFeatureCount = (features || []).filter(f => !f.properties?.isArrowhead).length;

  const renderNode = (node, depth = 0) => {
    const visible = !hiddenLayers.has(node.name);
    const expanded = expandedCats[node.id];
    const g = node.featureGroup || layerGroups[node.name];
    const hasChildren = node.children?.length > 0;
    const hasFeatures = g && g.total > 0;
    const isRenaming = renamingId === node.id;

    return (
      <div key={node.id} style={{ marginLeft: depth * 12 }}>
        <div className="flex items-center gap-1 px-1 py-0.5 rounded hover:bg-muted/50 group">
          <button onClick={() => toggleCat(node.id)} className="w-4 h-4 flex items-center justify-center text-muted-foreground flex-shrink-0">
            {(hasChildren || hasFeatures) ? (expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />) : <span className="w-3" />}
          </button>
          <Checkbox checked={visible} onCheckedChange={() => toggleLayer(node.name)} className="flex-shrink-0" data-testid={`layer-toggle-${node.name}`} />
          {isRenaming ? (
            <Input
              autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') renameFolder(node.id); if (e.key === 'Escape') setRenamingId(null); }}
              onBlur={() => renameFolder(node.id)}
              className="h-5 text-xs px-1 flex-1 min-w-0"
            />
          ) : (
            <span className="text-xs font-medium flex-1 truncate cursor-default flex items-center gap-1" title={node.name}
              onDoubleClick={() => { if (canEdit && !node.auto) { setRenamingId(node.id); setRenameVal(node.name); } }}>
              {visible ? <Eye className="w-3 h-3 text-primary flex-shrink-0" /> : <EyeOff className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
              {node.name}
            </span>
          )}
          {g && <span className="text-[10px] text-muted-foreground tabular-nums flex-shrink-0">{g.total}</span>}
          {/* Folder actions */}
          {canEdit && !node.auto && !isRenaming && (
            <div className="hidden group-hover:flex items-center gap-0.5 flex-shrink-0">
              <button onClick={() => { setShowNewFolder(true); setNewFolderParent(node.id); }} title="Add sub-folder" className="w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-primary">
                <FolderPlus className="w-3 h-3" />
              </button>
              <button onClick={() => { setRenamingId(node.id); setRenameVal(node.name); }} title="Rename" className="w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-primary">
                <Pencil className="w-2.5 h-2.5" />
              </button>
              <button onClick={() => deleteFolder(node.id)} title="Delete folder" className="w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-destructive">
                <Trash2 className="w-2.5 h-2.5" />
              </button>
            </div>
          )}
        </div>
        {/* Expanded children */}
        {expanded && (
          <>
            {(node.children || []).map(child => renderNode(child, depth + 1))}
            {g && g.features.slice(0, 30).map((f, i) => {
              const isMoving = movingFeatureIdx === f._idx;
              const featureHidden = hiddenFeatures.has(f._idx);
              const folderHidden = hiddenLayers.has(node.name);
              return (
                <div key={i} className="flex items-center gap-1 py-0.5 group/feat" style={{ marginLeft: (depth + 1) * 12 + 8 }} data-testid={`feature-item-${f._idx}`}>
                  <button
                    onClick={() => toggleFeature(f._idx)}
                    className={`w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 transition-colors ${folderHidden ? 'opacity-30 cursor-not-allowed' : featureHidden ? 'text-muted-foreground/50 hover:text-primary' : 'text-primary hover:text-primary/70'}`}
                    title={featureHidden ? 'Show feature' : 'Hide feature'}
                    data-testid={`feature-toggle-${f._idx}`}
                  >
                    {featureHidden ? <EyeOff className="w-2.5 h-2.5" /> : <Eye className="w-2.5 h-2.5" />}
                  </button>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: f.properties?.color || '#3b82f6', opacity: featureHidden ? 0.3 : 1 }} />
                  <span className={`text-[10px] flex-1 truncate ${featureHidden ? 'text-muted-foreground/40 line-through' : 'text-muted-foreground'}`}>
                    {f.properties?.iconType && ALL_ICONS[f.properties.iconType] && (
                      <span className="mr-0.5" style={{ opacity: featureHidden ? 0.3 : 1 }} dangerouslySetInnerHTML={{ __html: `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" style="display:inline;vertical-align:middle">${ALL_ICONS[f.properties.iconType].svg(f.properties.color || '#3b82f6')}</svg>` }} />
                    )}
                    {f.properties?.label || `${f.geometry?.type || 'Feature'} ${i + 1}`}
                  </span>
                  {canEdit && !isMoving && (
                    <button onClick={() => setMovingFeatureIdx(f._idx)} title="Move to folder" className="hidden group-hover/feat:flex w-4 h-4 items-center justify-center text-muted-foreground hover:text-primary flex-shrink-0">
                      <MoveRight className="w-2.5 h-2.5" />
                    </button>
                  )}
                  {isMoving && (
                    <div className="absolute right-0 bg-popover border rounded shadow-xl z-50 p-1 text-[10px] w-28">
                      {allLayerNames.filter(n => n !== node.name).map(n => (
                        <button key={n} onClick={() => moveFeatureToFolder(f._idx, n)} className="block w-full text-left px-2 py-1 rounded hover:bg-accent truncate">{n}</button>
                      ))}
                      {(folders || []).filter(fl => fl.name !== node.name).map(fl => (
                        <button key={fl.id} onClick={() => moveFeatureToFolder(f._idx, fl.name)} className="block w-full text-left px-2 py-1 rounded hover:bg-accent truncate">{fl.name}</button>
                      ))}
                      <button onClick={() => setMovingFeatureIdx(null)} className="block w-full text-center px-2 py-1 text-muted-foreground">Cancel</button>
                    </div>
                  )}
                </div>
              );
            })}
            {g && g.features.length > 30 && (
              <div className="text-[10px] text-muted-foreground italic" style={{ marginLeft: (depth + 1) * 12 + 8 }}>+{g.features.length - 30} more</div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-1" data-testid="layer-tree">
      {/* Toggle all + Create folder button */}
      <div className="flex items-center gap-2 px-1 pb-1.5 border-b mb-1">
        <Checkbox checked={allVisible} onCheckedChange={toggleAll} data-testid="layer-tree-toggle-all" />
        <span className="text-xs font-medium flex-1">All Layers</span>
        <span className="text-[10px] text-muted-foreground">{allFeatureCount}</span>
        {canEdit && (
          <button onClick={() => { setShowNewFolder(true); setNewFolderParent(null); }} title="Create folder" className="text-muted-foreground hover:text-primary" data-testid="create-folder-btn">
            <FolderPlus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Create folder input */}
      {showNewFolder && (
        <div className="px-1 pb-2 space-y-1.5 border-b mb-1" data-testid="new-folder-form">
          <div className="flex items-center gap-1.5">
            <FolderPlus className="w-3.5 h-3.5 text-primary flex-shrink-0" />
            <span className="text-xs font-medium">New Folder</span>
            {newFolderParent && (
              <span className="text-[10px] text-muted-foreground truncate">in: {(folders || []).find(f => f.id === newFolderParent)?.name}</span>
            )}
          </div>
          <Input
            autoFocus
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderParent(null); } }}
            placeholder="Folder name (e.g. Day 1, Scenario A)"
            className="h-7 text-xs"
            data-testid="new-folder-name"
          />
          <div className="flex gap-1">
            <Button size="sm" className="h-6 text-[10px] flex-1" onClick={createFolder} data-testid="new-folder-save">Create</Button>
            <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => { setShowNewFolder(false); setNewFolderParent(null); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Folder tree */}
      {folderTree.length === 0 && !allFeatureCount ? (
        <div className="text-center py-6 text-muted-foreground">
          <FolderTree className="w-6 h-6 mx-auto mb-2 opacity-30" />
          <p className="text-xs">No layers yet.</p>
          {canEdit && <p className="text-[10px] mt-1">Draw on the map or create a folder to organize features.</p>}
        </div>
      ) : (
        <div className="relative">
          {folderTree.map(node => renderNode(node, 0))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ICON PICKER — categorised flyout
   ══════════════════════════════════════════════════════════════════════════ */

function IconPicker({ activeIcon, activeColor, setActiveIcon }) {
  const [open, setOpen] = useState(false);
  const current = ALL_ICONS[activeIcon] || ALL_ICONS.marker;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 px-2 h-7 rounded border text-xs hover:bg-muted transition-colors"
        data-testid="icon-picker-btn"
        title="Select marker icon"
      >
        <span dangerouslySetInnerHTML={{ __html: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">${current.svg(activeColor)}</svg>` }} />
        <span className="max-w-[60px] truncate">{current.label}</span>
        <ChevronDown className="w-3 h-3 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute left-0 top-8 z-[3000] bg-popover border rounded-xl shadow-2xl p-3 w-72 max-h-80 overflow-y-auto" data-testid="icon-picker-dropdown">
          {Object.entries(ICON_CATEGORIES).map(([catName, icons]) => (
            <div key={catName} className="mb-2">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">{catName}</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(icons).map(([key, { label, svg }]) => (
                  <button
                    key={key}
                    onClick={() => { setActiveIcon(key); setOpen(false); }}
                    title={label}
                    className={`flex flex-col items-center justify-center w-11 h-11 rounded border transition-all ${
                      activeIcon === key
                        ? 'border-primary bg-primary/10 ring-1 ring-primary'
                        : 'border-transparent hover:bg-muted hover:border-muted-foreground/20'
                    }`}
                    data-testid={`map-icon-${key}`}
                  >
                    <span dangerouslySetInnerHTML={{ __html: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">${svg(activeColor)}</svg>` }} />
                    <span className="text-[8px] text-muted-foreground truncate w-full text-center">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════════════ */

export default function ExerciseMap({ exerciseId, mapApi, canEdit = false }) {
  const { user } = useAuth();
  // V14.10.3 — Only super_admin / hub_admin see the "last edit" indicator
  // and can clear it. Simcell controllers can edit but do NOT see the badge.
  const isSuperAdmin = (user?.roles || []).some(r => ['super_admin', 'hub_admin'].includes(r));
  const mapRef = useRef(null);
  const [mapData,    setMapData]    = useState(null);
  const mapDataRef = useRef(null); // always-current reference for callbacks
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [coords,     setCoords]     = useState(null);
  const [measurement, setMeasurement] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showMini,   setShowMini]   = useState(false);

  /* Searching */
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);

  /* V14.5 — track deleted featureIds for safe concurrent multi-user saves */
  const deletedFeatureIdsRef = useRef([]);
  const handleFeatureDeleted = useCallback((fid) => {
    deletedFeatureIdsRef.current.push(fid);
  }, []);

  /* Drawing */
  const [activeColor, setActiveColor] = useState('#3b82f6');
  const [arrowMode,   setArrowMode]   = useState(false);
  const [activeIcon,  setActiveIcon]  = useState('marker');
  const colorRef     = useRef('#3b82f6');
  const arrowModeRef = useRef(false);
  const iconRef      = useRef('marker');
  useEffect(() => { colorRef.current = activeColor; },   [activeColor]);
  useEffect(() => { arrowModeRef.current = arrowMode; }, [arrowMode]);
  useEffect(() => { iconRef.current = activeIcon; }, [activeIcon]);
  useEffect(() => { mapDataRef.current = mapData; }, [mapData]);

  /* Go-to-coords */
  const [showGoto, setShowGoto] = useState(false);
  const [gotoLat,  setGotoLat]  = useState('');
  const [gotoLng,  setGotoLng]  = useState('');

  /* Manual marker placement */
  const [showManualMarker, setShowManualMarker] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [manualLabel, setManualLabel] = useState('');

  /* Selected feature details */
  const [selectedFeature, setSelectedFeature] = useState(null);

  /* Layer tree visibility — always visible for admin */
  const [activePanel, setActivePanel] = useState('layers'); // 'layers' | 'userview'
  const [hiddenLayers, setHiddenLayers] = useState(new Set());
  const [hiddenFeatures, setHiddenFeatures] = useState(new Set());

  /* Folders state (persisted to backend) */
  const [folders, setFolders] = useState([]);

  /* Participant visibility control (admin only) */
  const [participantHiddenLayers, setParticipantHiddenLayers] = useState(new Set());
  const [participantHiddenFeatureIds, setParticipantHiddenFeatureIds] = useState(new Set());
  const [previewAsParticipant, setPreviewAsParticipant] = useState(false);
  const [participantExpandedCats, setParticipantExpandedCats] = useState({});

  /* V14.6 — "Earth View" briefing mode (satellite basemap + boosted fills) */
  const [briefingMode, setBriefingMode] = useState(false);

  /* File input ref for import */
  const fileInputRef = useRef(null);

  /* Label modal */
  const [pendingLayer,  setPendingLayer]  = useState(null);
  const [labelTxt,      setLabelTxt]      = useState('');
  const [descTxt,       setDescTxt]       = useState('');
  const [targetFolder,  setTargetFolder]  = useState('Drawings');
  const pendingDIRef = useRef(null);

  /* Load map */
  useEffect(() => {
    let dead = false;
    mapApi.getMap(exerciseId)
      .then(d => {
        if (!dead) {
          setMapData(d);
          setFolders(d.folders || []);
          setParticipantHiddenLayers(new Set(d.participant_hidden_layers || []));
          setParticipantHiddenFeatureIds(new Set(d.participant_hidden_features || []));
          setLoading(false);
        }
      })
      .catch(() => setLoading(false));
    return () => { dead = true; };
  }, [exerciseId]);

  /* V14.5 — Listen for Socket.IO map_updated / map_feature_* events from
     other admins editing the same exercise simultaneously. We refetch the
     full doc on any such event (debounced) so concurrent collaborators see
     each other's work without manual refresh. */
  const lastLocalSaveAtRef = useRef(0);
  useEffect(() => {
    if (!exerciseId) return;
    let pending = null;
    const refetch = () => {
      if (pending) return;
      pending = setTimeout(async () => {
        pending = null;
        try {
          const d = await mapApi.getMap(exerciseId);
          setMapData(d);
          if (d.folders) setFolders(d.folders);
        } catch (_) { /* ignore */ }
      }, 600);
    };

    // Resolve current user_id once (stored as JSON under 'ems_user')
    let myUid = null;
    try {
      const raw = localStorage.getItem('ems_user');
      if (raw) myUid = JSON.parse(raw)?.user_id || null;
    } catch (_) { myUid = null; }

    const handler = (e) => {
      const detail = e?.detail || {};
      if (detail.exercise_id && detail.exercise_id !== exerciseId) return;
      if (!['map_updated', 'map_feature_added', 'map_feature_deleted'].includes(detail.type)) return;
      // Skip our own broadcasts (server echoes back to the saver)
      if (myUid && detail.by_user_id === myUid) return;
      // Skip if a local save just happened in the last 1.5s — protects active edit sessions
      if (Date.now() - lastLocalSaveAtRef.current < 1500) return;
      refetch();
    };
    window.addEventListener('ws-message', handler);
    return () => {
      window.removeEventListener('ws-message', handler);
      if (pending) clearTimeout(pending);
    };
  }, [exerciseId]);

  const persist = useCallback(async (gj, foldersOverride) => {
    if (!mapRef.current) return;
    const { lat, lng } = mapRef.current.getCenter();
    setSaving(true);
    try {
      const payload = { geojson: gj, center: [lat, lng], zoom: mapRef.current.getZoom() };
      if (foldersOverride !== undefined) payload.folders = foldersOverride;
      else payload.folders = folders;

      // V14.5: include deleted featureIds so the server merges + applies deletes
      // safely without losing other admins' concurrent work.
      if (deletedFeatureIdsRef.current.length > 0) {
        payload.deleted_feature_ids = [...deletedFeatureIdsRef.current];
        deletedFeatureIdsRef.current = [];
      }

      const upd = await mapApi.updateMap(exerciseId, payload);
      lastLocalSaveAtRef.current = Date.now();
      setMapData(upd);
      if (upd.folders) setFolders(upd.folders);
    } catch { toast.error('Failed to save map'); }
    finally { setSaving(false); }
  }, [exerciseId, folders]);

  /* Persist participant visibility settings to backend */
  const persistParticipantVisibility = useCallback(async (hiddenLayerSet, hiddenFeatureSet) => {
    if (!mapRef.current) return;
    const { lat, lng } = mapRef.current.getCenter();
    setSaving(true);
    try {
      const payload = {
        participant_hidden_layers: Array.from(hiddenLayerSet),
        participant_hidden_features: Array.from(hiddenFeatureSet),
        center: [lat, lng],
        zoom: mapRef.current.getZoom(),
      };
      const upd = await mapApi.updateMap(exerciseId, payload);
      setMapData(upd);
      setParticipantDirty(false);
      toast.success('Pushed to participants');
    } catch { toast.error('Failed to save participant visibility'); }
    finally { setSaving(false); }
  }, [exerciseId]);

  /* Track if participant visibility has unpushed changes */
  const [participantDirty, setParticipantDirty] = useState(false);

  const toggleParticipantLayer = useCallback((layerName) => {
    setParticipantHiddenLayers(prev => {
      const next = new Set(prev);
      if (next.has(layerName)) next.delete(layerName);
      else next.add(layerName);
      return next;
    });
    setParticipantDirty(true);
  }, []);

  const toggleParticipantFeature = useCallback((featureId) => {
    setParticipantHiddenFeatureIds(prev => {
      const next = new Set(prev);
      if (next.has(featureId)) next.delete(featureId);
      else next.add(featureId);
      return next;
    });
    setParticipantDirty(true);
  }, []);

  const pushToParticipants = useCallback(() => {
    persistParticipantVisibility(participantHiddenLayers, participantHiddenFeatureIds);
  }, [persistParticipantVisibility, participantHiddenLayers, participantHiddenFeatureIds]);

  /* ── Participant polling: auto-refresh map when admin pushes changes ── */
  const participantMapVersionRef = useRef(0);
  useEffect(() => {
    if (canEdit || !exerciseId || !mapApi.getMapVersion) return;
    let active = true;
    const poll = async () => {
      try {
        const versionData = await mapApi.getMapVersion(exerciseId);
        const serverVersion = versionData?.participant_map_version || 0;
        if (participantMapVersionRef.current > 0 && serverVersion > participantMapVersionRef.current) {
          // Admin pushed changes — reload map
          const d = await mapApi.getMap(exerciseId);
          if (active && d) {
            setMapData(d);
            setParticipantHiddenLayers(new Set(d.participant_hidden_layers || []));
            setParticipantHiddenFeatureIds(new Set(d.participant_hidden_features || []));
            toast.info('Map updated by exercise control');
          }
        }
        participantMapVersionRef.current = serverVersion;
      } catch { /* silent poll failure */ }
    };
    // Initial fetch to set baseline version
    poll();
    const interval = setInterval(poll, 8000); // poll every 8 seconds
    return () => { active = false; clearInterval(interval); };
  }, [canEdit, exerciseId]);

  /* Ref to hold hidden features during create→label flow */
  const pendingHiddenRef = useRef([]);

  const handleCreated = useCallback((layer, drawnItems, meas, hiddenOriginalFeatures) => {
    if (meas) setMeasurement(meas);
    setPendingLayer(layer);
    pendingDIRef.current = drawnItems;
    pendingHiddenRef.current = hiddenOriginalFeatures || [];
    setLabelTxt(''); setDescTxt('');
  }, []);

  /* Persist map, preserving ground overlay features and hidden features that aren't in drawnItems */
  const persistWithOverlays = useCallback((di, hiddenOriginalFeatures) => {
    // Update circle radius for any circles that were resized
    di.eachLayer(layer => {
      if (layer instanceof L.Circle && layer.feature) {
        layer.feature.properties.radius = layer.getRadius();
      }
    });
    const gj = toCleanGeoJSON(di);
    const existingFeatures = mapDataRef.current?.geojson?.features || [];
    const groundOverlays = existingFeatures.filter(f => f.properties?.isGroundOverlay);
    // Merge back ground overlays
    if (groundOverlays.length > 0) {
      gj.features = [...gj.features, ...groundOverlays];
    }
    // Merge back hidden features (excluded from drawnItems to prevent edit crash)
    const hiddenFeats = hiddenOriginalFeatures || [];
    if (hiddenFeats.length > 0) {
      // Strip internal _cleanIdx before saving
      const cleaned = hiddenFeats.map(f => {
        const props = { ...f.properties };
        delete props._cleanIdx;
        return { ...f, properties: props };
      });
      gj.features = [...gj.features, ...cleaned];
    }
    persist(gj);
  }, [persist]);

  const handleChanged = useCallback((drawnItems, hiddenOriginalFeatures) => {
    persistWithOverlays(drawnItems, hiddenOriginalFeatures);
  }, [persistWithOverlays]);

  const confirmLabel = useCallback(() => {
    const layer = pendingLayer, di = pendingDIRef.current;
    if (!layer || !di) return;
    layer.feature = layer.feature || { type: 'Feature', geometry: null, properties: {} };
    layer.feature.properties.layer = targetFolder || 'Drawings';
    if (labelTxt.trim()) {
      layer.feature.properties.label = labelTxt.trim();
      layer.bindTooltip(labelTxt.trim(), { permanent: true, direction: 'top', className: 'map-label-tip' });
    }
    if (descTxt.trim()) {
      layer.feature.properties.description = descTxt.trim();
      const popupHtml = labelTxt.trim() ? `<b>${labelTxt.trim()}</b><br>${descTxt.trim()}` : descTxt.trim();
      layer.bindPopup(DOMPurify.sanitize(popupHtml));
    }
    di.addLayer(layer);
    persistWithOverlays(di, pendingHiddenRef.current);
    setPendingLayer(null);
  }, [pendingLayer, labelTxt, descTxt, persistWithOverlays, targetFolder]);

  const skipLabel = useCallback(() => {
    const layer = pendingLayer, di = pendingDIRef.current;
    if (!layer || !di) return;
    layer.feature = layer.feature || { type: 'Feature', geometry: null, properties: {} };
    layer.feature.properties.layer = targetFolder || 'Drawings';
    di.addLayer(layer);
    persistWithOverlays(di, pendingHiddenRef.current);
    setPendingLayer(null);
  }, [pendingLayer, persistWithOverlays, targetFolder]);

  /* Geocoding search */
  const runSearch = useCallback(async () => {
    if (!searchQ.trim()) return;
    setSearchBusy(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQ)}&format=json&limit=6`, { headers: { 'Accept-Language': 'en' } });
      setSearchResults(await r.json());
    } catch { toast.error('Search failed'); }
    finally { setSearchBusy(false); }
  }, [searchQ]);

  const flyToResult = useCallback(r => {
    mapRef.current?.flyTo([+r.lat, +r.lon], 13);
    setSearchResults([]); setSearchQ('');
  }, []);

  const gotoCoords = useCallback(() => {
    const lat = parseFloat(gotoLat), lng = parseFloat(gotoLng);
    if (isNaN(lat) || isNaN(lng)) { toast.error('Invalid coordinates'); return; }
    mapRef.current?.flyTo([lat, lng], 13);
    setShowGoto(false); setGotoLat(''); setGotoLng('');
  }, [gotoLat, gotoLng]);

  const placeManualMarker = useCallback(() => {
    const lat = parseFloat(manualLat), lng = parseFloat(manualLng);
    if (isNaN(lat) || isNaN(lng)) { toast.error('Invalid coordinates'); return; }
    const feature = {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [lng, lat] },
      properties: {
        color: activeColor,
        iconType: activeIcon,
        label: manualLabel.trim() || `Point (${lat.toFixed(4)}, ${lng.toFixed(4)})`,
        layer: 'Manual',
      },
    };
    const currentFeatures = mapData?.geojson?.features || [];
    persist({ type: 'FeatureCollection', features: [...currentFeatures, feature] });
    mapRef.current?.flyTo([lat, lng], 13);
    setShowManualMarker(false);
    setManualLat(''); setManualLng(''); setManualLabel('');
    toast.success('Marker placed');
  }, [manualLat, manualLng, manualLabel, activeColor, activeIcon, mapData, persist]);

  /* Export KML */
  const exportKML = useCallback(() => {
    const features = mapData?.geojson?.features || [];
    const clean = features.filter(f => !f.properties?.isArrowhead);
    if (!clean.length) { toast.error('No features to export'); return; }

    // Group by layer
    const groups = {};
    clean.forEach(f => {
      const layer = f.properties?.layer || 'Default';
      if (!groups[layer]) groups[layer] = [];
      groups[layer].push(f);
    });

    const escXml = s => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    let folders = '';
    for (const [name, feats] of Object.entries(groups)) {
      const placemarks = feats.map(f => {
        const nm = escXml(f.properties?.label);
        const desc = escXml(f.properties?.description);
        let geom = '';
        if (f.geometry?.type === 'Point') {
          const [lng, lat] = f.geometry.coordinates;
          geom = `<Point><coordinates>${lng},${lat},0</coordinates></Point>`;
        } else if (f.geometry?.type === 'LineString') {
          const coords = f.geometry.coordinates.map(c => `${c[0]},${c[1]},0`).join(' ');
          geom = `<LineString><coordinates>${coords}</coordinates></LineString>`;
        } else if (f.geometry?.type === 'Polygon') {
          const coords = f.geometry.coordinates[0].map(c => `${c[0]},${c[1]},0`).join(' ');
          geom = `<Polygon><outerBoundaryIs><LinearRing><coordinates>${coords}</coordinates></LinearRing></outerBoundaryIs></Polygon>`;
        }
        return `<Placemark><name>${nm}</name><description>${desc}</description>${geom}</Placemark>`;
      }).join('\n');
      folders += `<Folder><name>${escXml(name)}</name>\n${placemarks}\n</Folder>\n`;
    }
    const kml = `<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2">\n<Document><name>Exercise Map Export</name>\n${folders}</Document>\n</kml>`;
    const blob = new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'exercise-map.kml'; a.click();
    URL.revokeObjectURL(url);
    toast.success('KML file downloaded');
  }, [mapData]);

  /* Import KML/KMZ */
  const importFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      let kmlText;
      let imageMap = null; // Map of filename -> data URL for KMZ images
      const ext = file.name.split('.').pop().toLowerCase();

      if (ext === 'kmz') {
        // KMZ is a ZIP — extract the .kml file and any images inside
        const zip = await JSZip.loadAsync(file);
        const kmlFile = Object.values(zip.files).find(f => f.name.endsWith('.kml'));
        if (!kmlFile) { toast.error('No .kml file found inside KMZ'); return; }
        kmlText = await kmlFile.async('text');

        // Extract images from KMZ archive
        imageMap = new Map();
        const imageExts = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tif', '.tiff', '.svg'];
        for (const [path, zipEntry] of Object.entries(zip.files)) {
          if (zipEntry.dir) continue;
          const lowerPath = path.toLowerCase();
          if (imageExts.some(ext => lowerPath.endsWith(ext))) {
            try {
              const blob = await zipEntry.async('blob');
              const mimeType = lowerPath.endsWith('.png') ? 'image/png'
                : lowerPath.endsWith('.svg') ? 'image/svg+xml'
                : lowerPath.endsWith('.gif') ? 'image/gif'
                : lowerPath.endsWith('.bmp') ? 'image/bmp'
                : 'image/jpeg';
              const dataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(new Blob([blob], { type: mimeType }));
              });
              // Store with both full path and filename for flexible matching
              imageMap.set(path, dataUrl);
              imageMap.set(path.split('/').pop(), dataUrl);
            } catch (imgErr) {
              console.warn('Failed to extract image:', path, imgErr);
            }
          }
        }
        if (imageMap.size > 0) {
          console.log(`Extracted ${imageMap.size / 2} images from KMZ`);
        }
      } else {
        kmlText = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = ev => res(ev.target.result);
          reader.onerror = rej;
          reader.readAsText(file);
        });
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(kmlText, 'text/xml');

      // Check for parse errors
      const parseError = doc.querySelector('parsererror');
      if (parseError) { toast.error('Invalid KML/XML file'); return; }

      const newFeatures = parseKMLDocument(doc, imageMap);
      if (!newFeatures.length) { toast.error('No valid placemarks found'); return; }

      const overlayCount = newFeatures.filter(f => f.properties?.isGroundOverlay).length;
      const currentFeatures = mapData?.geojson?.features || [];

      // V14.6 — when there are already features on the map, ask whether to
      // REPLACE (recommended after the V14.6 styling fix so old skeleton imports
      // get cleared and the new fills/icons take effect) or APPEND.
      let mergedFeatures;
      if (currentFeatures.length > 0) {
        const choice = window.confirm(
          `This map already has ${currentFeatures.length} feature(s).\n\n` +
          `OK = REPLACE existing features with this import (recommended for re-imports after styling fixes).\n` +
          `Cancel = ADD this import to the existing features.`
        );
        if (choice) {
          // Queue every existing featureId for deletion in the next persist() so the
          // backend merge actually wipes the old skeleton instead of keeping it.
          const existingIds = currentFeatures
            .map(f => f.properties?.featureId || f.properties?.id)
            .filter(Boolean);
          deletedFeatureIdsRef.current = [...deletedFeatureIdsRef.current, ...existingIds];
          mergedFeatures = newFeatures;
        } else {
          mergedFeatures = [...currentFeatures, ...newFeatures];
        }
      } else {
        mergedFeatures = newFeatures;
      }
      persist({ type: 'FeatureCollection', features: mergedFeatures });

      // Zoom to fit imported features
      const bounds = L.latLngBounds([]);
      newFeatures.forEach(f => {
        if (f.properties?.isGroundOverlay && f.properties?.bounds) {
          bounds.extend(L.latLng(f.properties.bounds[0]));
          bounds.extend(L.latLng(f.properties.bounds[1]));
        } else if (f.geometry?.type === 'Point') {
          bounds.extend(L.latLng(f.geometry.coordinates[1], f.geometry.coordinates[0]));
        } else if (f.geometry?.coordinates) {
          const coords = f.geometry.type === 'Polygon' ? f.geometry.coordinates[0] : f.geometry.coordinates;
          coords.forEach(c => bounds.extend(L.latLng(c[1], c[0])));
        }
      });
      if (bounds.isValid()) mapRef.current?.fitBounds(bounds, { padding: [40, 40] });

      const msg = `Imported ${newFeatures.length} feature(s)${overlayCount ? ` (${overlayCount} overlay(s))` : ''} from ${ext.toUpperCase()}`;
      toast.success(msg);
    } catch (err) {
      console.error('Import error:', err);
      toast.error('Failed to parse file: ' + (err.message || 'Unknown error'));
    }
  }, [mapData, persist]);

  /* When previewing as participant OR actual participant, use ONLY participant hidden layers — completely independent from admin */
  const effectiveHiddenLayers = useMemo(() => {
    if (!canEdit) return participantHiddenLayers; // Actual participant: ONLY use participant config
    if (previewAsParticipant) return participantHiddenLayers; // Preview mode: ONLY use participant config
    return hiddenLayers; // Admin normal view
  }, [canEdit, hiddenLayers, participantHiddenLayers, previewAsParticipant]);

  /* When previewing as participant OR actual participant, only hide participant-hidden features */
  const effectiveHiddenFeatures = useMemo(() => {
    if (!canEdit || previewAsParticipant) {
      // Map participant feature IDs to indices
      const pHidden = new Set();
      const feats = mapData?.geojson?.features || [];
      feats.forEach((f, idx) => {
        const fid = f.properties?.featureId || f.properties?.id || `f_${idx}`;
        if (participantHiddenFeatureIds.has(fid)) pHidden.add(idx);
      });
      return pHidden;
    }
    return hiddenFeatures;
  }, [canEdit, hiddenFeatures, participantHiddenFeatureIds, previewAsParticipant, mapData]);

  /* Collect all layer names for the participant panel */
  const allMapLayerNames = useMemo(() => {
    const names = new Set();
    const feats = mapData?.geojson?.features || [];
    feats.filter(f => !f.properties?.isArrowhead).forEach(f => {
      names.add(f.properties?.layer || 'Unsorted');
    });
    return Array.from(names).sort();
  }, [mapData]);

  if (loading) return <div className="flex items-center justify-center h-96 text-muted-foreground">Loading map...</div>;

  const center = mapData?.center || [51.505, -0.09];
  const zoom   = mapData?.zoom   || 5;
  const features = mapData?.geojson?.features || [];

  return (
    <div className={isFullscreen ? 'fixed inset-0 z-[35] bg-background flex flex-col p-3 gap-3' : 'space-y-3'}>

      {/* ── Toolbar (admin/simcell only — participants see simplified controls) ── */}
      {canEdit ? (
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-card border rounded-lg">

        {/* Color palette */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground font-medium">Color:</span>
          {PALETTE.map(c => (
            <button key={c.hex} title={c.name} onClick={() => setActiveColor(c.hex)}
              className={`w-5 h-5 rounded-full transition-all flex-shrink-0 ${activeColor === c.hex ? 'ring-2 ring-offset-1 ring-primary scale-110' : 'opacity-60 hover:opacity-100 hover:scale-105'}`}
              style={{ background: c.hex, border: '1px solid rgba(0,0,0,0.2)' }} />
          ))}
          <input type="color" value={activeColor} onChange={e => setActiveColor(e.target.value)}
            title="Custom color" className="w-5 h-5 rounded cursor-pointer border-0 bg-transparent p-0"
            style={{ minWidth: 20 }} />
        </div>

        {/* Arrow mode */}
        <Button size="sm" variant={arrowMode ? 'default' : 'outline'} className="h-7 text-xs px-2 gap-1"
          onClick={() => setArrowMode(v => !v)} title="Arrow line mode">
          &#10148; Arrow {arrowMode && '\u25CF'}
        </Button>

        {/* Icon picker */}
        <IconPicker activeIcon={activeIcon} activeColor={activeColor} setActiveIcon={setActiveIcon} />

        <div className="flex-1 min-w-4" />

        {/* Location search */}
        <div className="relative flex items-center gap-1">
          <Input value={searchQ} onChange={e => setSearchQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runSearch()}
            placeholder="Search location..." className="h-7 text-xs w-44 pr-1" />
          <Button size="sm" variant="outline" className="h-7 px-2" onClick={runSearch} disabled={searchBusy}>
            <Search className="w-3 h-3" />
          </Button>
          {searchResults.length > 0 && (
            <div className="absolute top-8 left-0 w-80 bg-popover border rounded-lg shadow-xl z-[3000] overflow-hidden">
              {searchResults.map((r, i) => (
                <button key={i} className="w-full text-left px-3 py-2 text-xs hover:bg-accent truncate border-b last:border-0"
                  onClick={() => flyToResult(r)}>{r.display_name}</button>
              ))}
              <button className="w-full text-center py-1.5 text-xs text-muted-foreground hover:bg-accent"
                onClick={() => setSearchResults([])}>Close</button>
            </div>
          )}
        </div>

        {/* Go-to coordinates */}
        <div className="relative">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setShowGoto(v => !v)} title="Go to coordinates">
            <Crosshair className="w-3 h-3" /> Go to
          </Button>
          {showGoto && (
            <div className="absolute right-0 top-8 bg-popover border rounded-xl shadow-xl z-[3000] p-3 w-52 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Jump to coordinates</p>
              <Input placeholder="Latitude (e.g. 51.5)" value={gotoLat} onChange={e => setGotoLat(e.target.value)} className="h-7 text-xs" onKeyDown={e => e.key === 'Enter' && gotoCoords()} />
              <Input placeholder="Longitude (e.g. -0.1)" value={gotoLng} onChange={e => setGotoLng(e.target.value)} className="h-7 text-xs" onKeyDown={e => e.key === 'Enter' && gotoCoords()} />
              <div className="flex gap-1.5">
                <Button size="sm" className="flex-1 h-7 text-xs" onClick={gotoCoords}>Fly there</Button>
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setShowGoto(false)}><X className="w-3 h-3" /></Button>
              </div>
            </div>
          )}
        </div>

        {/* Manual marker */}
        <div className="relative">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setShowManualMarker(v => !v)} title="Place marker at coordinates" data-testid="manual-marker-btn">
            <MapPin className="w-3 h-3" /> Place
          </Button>
          {showManualMarker && (
            <div className="absolute right-0 top-8 bg-popover border rounded-xl shadow-xl z-[3000] p-3 w-56 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Place marker at coordinates</p>
              <Input placeholder="Latitude" value={manualLat} onChange={e => setManualLat(e.target.value)} className="h-7 text-xs" data-testid="manual-lat" />
              <Input placeholder="Longitude" value={manualLng} onChange={e => setManualLng(e.target.value)} className="h-7 text-xs" data-testid="manual-lng" />
              <Input placeholder="Label (optional)" value={manualLabel} onChange={e => setManualLabel(e.target.value)} className="h-7 text-xs" data-testid="manual-label" onKeyDown={e => e.key === 'Enter' && placeManualMarker()} />
              <div className="flex gap-1.5">
                <Button size="sm" className="flex-1 h-7 text-xs" onClick={placeManualMarker} data-testid="place-marker-btn">Place</Button>
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setShowManualMarker(false)}><X className="w-3 h-3" /></Button>
              </div>
            </div>
          )}
        </div>

        {/* KML Import/Export */}
        <>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={exportKML} title="Export as KML" data-testid="export-kml-btn">
            <Download className="w-3 h-3" /> KML
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => fileInputRef.current?.click()} title="Import KML or KMZ file" data-testid="import-kml-btn">
            <Upload className="w-3 h-3" /> Import
          </Button>
          <input ref={fileInputRef} type="file" accept=".kml,.kmz,.xml" className="hidden" onChange={importFile} data-testid="import-kml-input" />
        </>

        {/* Preview as participant toggle */}
        <Button size="sm" variant={previewAsParticipant ? 'default' : 'outline'}
          className={`h-7 px-2 gap-1 ${previewAsParticipant ? 'ring-2 ring-amber-400 bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
          onClick={() => setPreviewAsParticipant(v => !v)}
          title="Preview map as participant would see it"
          data-testid="preview-participant-btn">
          <Eye className="w-3 h-3" />
          <span className="text-xs hidden sm:inline">{previewAsParticipant ? 'Exit Preview' : 'Preview'}</span>
        </Button>

        {/* V14.6 — Earth View (briefing mode) toggle */}
        <Button size="sm" variant={briefingMode ? 'default' : 'outline'}
          className={`h-7 px-2 gap-1 ${briefingMode ? 'ring-2 ring-emerald-400 bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
          onClick={() => setBriefingMode(v => !v)}
          title="Match Google Earth view: satellite basemap + boosted polygon fills"
          data-testid="earth-view-btn">
          <Globe className="w-3 h-3" />
          <span className="text-xs hidden sm:inline">{briefingMode ? 'Exit Earth View' : 'Earth View'}</span>
        </Button>

        {/* Mini-map toggle */}
        <Button size="sm" variant={showMini ? 'default' : 'outline'} className="h-7 px-2"
          onClick={() => setShowMini(v => !v)} title="Toggle mini-map">
          <MapIcon className="w-3 h-3" />
        </Button>

        {/* Fullscreen */}
        <Button size="sm" variant="outline" className="h-7 px-2"
          onClick={() => setIsFullscreen(v => !v)} title="Toggle fullscreen">
          {isFullscreen ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
        </Button>
      </div>
      ) : (
        /* Participant minimal toolbar — only fullscreen */
        <div className="flex items-center justify-between px-3 py-2 bg-card border rounded-lg">
          <span className="text-xs font-semibold text-muted-foreground">Exercise Map</span>
          <Button size="sm" variant="outline" className="h-7 px-2"
            onClick={() => setIsFullscreen(v => !v)} title="Toggle fullscreen">
            {isFullscreen ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
          </Button>
        </div>
      )}

      {/* ── Map + Fixed Left Panel side-by-side ── */}
      <div className={`flex gap-0 ${isFullscreen ? 'flex-1' : ''}`}>

        {/* Left Panel: Always visible for admins — Layers + User View tabs */}
        {canEdit && (
          <div className={`w-64 flex-shrink-0 border rounded-l-lg bg-card overflow-hidden flex flex-col ${isFullscreen ? '' : 'h-[520px]'}`} data-testid="map-left-panel">
            {/* Panel tabs */}
            <div className="flex border-b bg-muted/30">
              <button
                className={`flex-1 px-3 py-2.5 text-xs font-medium text-center transition-all ${activePanel === 'layers' ? 'bg-background text-primary border-b-2 border-primary shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
                onClick={() => setActivePanel('layers')}
                data-testid="panel-layers-tab"
              >
                <FolderTree className="w-3 h-3 inline mr-1.5" />Layers
              </button>
              <button
                className={`flex-1 px-3 py-2.5 text-xs font-medium text-center transition-all ${activePanel === 'userview' ? 'bg-background text-primary border-b-2 border-primary shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
                onClick={() => setActivePanel('userview')}
                data-testid="panel-user-view-tab"
              >
                <Eye className="w-3 h-3 inline mr-1.5" />User View
              </button>
            </div>

            {/* Layers content */}
            {activePanel === 'layers' && (
              <ScrollArea className="flex-1">
                <div className="p-2">
                  <LayerTree
                  features={features}
                  folders={folders}
                  setFolders={setFolders}
                  hiddenLayers={hiddenLayers}
                  setHiddenLayers={setHiddenLayers}
                  hiddenFeatures={hiddenFeatures}
                  setHiddenFeatures={setHiddenFeatures}
                  canEdit={canEdit}
                  onPersistFolders={(updatedFolders) => {
                    if (!mapRef.current) return;
                    const { lat, lng } = mapRef.current.getCenter();
                    mapApi.updateMap(exerciseId, {
                      geojson: mapData?.geojson || { type: 'FeatureCollection', features: [] },
                      center: [lat, lng],
                      zoom: mapRef.current.getZoom(),
                      folders: updatedFolders,
                    }).then(upd => { setMapData(upd); if (upd.folders) setFolders(upd.folders); }).catch(() => toast.error('Failed to save folders'));
                  }}
                  onMoveFeature={(fromLayer, toLayer, featureIdx) => {
                    const feats = [...(mapData?.geojson?.features || [])];
                    if (featureIdx !== undefined && featureIdx !== null) {
                      const clean = feats.filter(f => !f.properties?.isArrowhead);
                      if (clean[featureIdx]) {
                        clean[featureIdx].properties = { ...clean[featureIdx].properties, layer: toLayer };
                      }
                    } else if (fromLayer) {
                      feats.forEach(f => { if (f.properties?.layer === fromLayer) f.properties.layer = toLayer; });
                    }
                    persist({ type: 'FeatureCollection', features: feats });
                  }}
                />
              </div>
            </ScrollArea>
            )}

            {/* User View content — control what participants see */}
            {activePanel === 'userview' && (
              <ScrollArea className="flex-1">
                <div className="p-2 space-y-1">
                  <div className="flex items-center gap-2 px-1 pb-2 mb-1 border-b">
                    <Eye className="w-3.5 h-3.5 text-primary" />
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Participant Visibility</span>
                  </div>
                  {allMapLayerNames.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic py-4 text-center">No layers on map yet</p>
                  ) : (
                    <>
                      {(() => {
                        const feats = mapData?.geojson?.features || [];
                        const groups = {};
                        feats.filter(f => !f.properties?.isArrowhead).forEach((f, idx) => {
                          const layer = f.properties?.layer || 'Unsorted';
                          if (!groups[layer]) groups[layer] = [];
                          const fid = f.properties?.featureId || f.properties?.id || `f_${idx}`;
                          groups[layer].push({ ...f, _idx: idx, _fid: fid });
                        });
                        return allMapLayerNames.map(layerName => {
                          const layerVisible = !participantHiddenLayers.has(layerName);
                          const layerFeats = groups[layerName] || [];
                          const expanded = participantExpandedCats[layerName];
                          return (
                            <div key={layerName}>
                              <div className="flex items-center gap-1.5 px-1 py-1 rounded hover:bg-muted/50">
                                <button onClick={() => setParticipantExpandedCats(p => ({ ...p, [layerName]: !p[layerName] }))} className="w-4 h-4 flex items-center justify-center text-muted-foreground flex-shrink-0">
                                  {layerFeats.length > 0 ? (expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />) : <span className="w-3" />}
                                </button>
                                <Checkbox checked={layerVisible} onCheckedChange={() => toggleParticipantLayer(layerName)} className="flex-shrink-0" data-testid={`participant-layer-${layerName}`} />
                                <button onClick={() => toggleParticipantLayer(layerName)} className={`flex items-center gap-1 flex-shrink-0 ${layerVisible ? 'text-primary' : 'text-muted-foreground/50'}`}>
                                  {layerVisible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                </button>
                                <span className="text-xs font-medium flex-1 truncate">{layerName}</span>
                                <span className="text-[10px] text-muted-foreground tabular-nums flex-shrink-0">{layerFeats.length}</span>
                              </div>
                              {expanded && layerFeats.map(f => {
                                const featureHidden = participantHiddenFeatureIds.has(f._fid);
                                const parentHidden = !layerVisible;
                                return (
                                  <div key={f._fid} className="flex items-center gap-1.5 py-0.5 ml-8" data-testid={`participant-feature-${f._fid}`}>
                                    <button
                                      onClick={() => !parentHidden && toggleParticipantFeature(f._fid)}
                                      className={`w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 ${parentHidden ? 'opacity-30 cursor-not-allowed' : featureHidden ? 'text-muted-foreground/50 hover:text-primary' : 'text-primary hover:text-primary/70'}`}
                                      disabled={parentHidden}
                                    >
                                      {featureHidden || parentHidden ? <EyeOff className="w-2.5 h-2.5" /> : <Eye className="w-2.5 h-2.5" />}
                                    </button>
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: f.properties?.color || '#3b82f6', opacity: featureHidden || parentHidden ? 0.3 : 1 }} />
                                    <span className={`text-[10px] flex-1 truncate ${featureHidden || parentHidden ? 'text-muted-foreground/40 line-through' : 'text-muted-foreground'}`}>
                                      {f.properties?.label || `${f.geometry?.type || 'Feature'}`}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        });
                      })()}
                    </>
                  )}
                  {/* Push + Preview controls at bottom */}
                  <div className="pt-2 mt-2 border-t space-y-2">
                    {/* Push button */}
                    <Button
                      size="sm"
                      className={`w-full h-8 text-xs gap-1.5 ${participantDirty ? 'bg-amber-500 hover:bg-amber-600 text-white animate-pulse' : ''}`}
                      onClick={pushToParticipants}
                      disabled={saving || !participantDirty}
                      data-testid="push-to-participants-btn"
                    >
                      <Upload className="w-3 h-3" />
                      {saving ? 'Pushing...' : participantDirty ? 'Push to Participants' : 'Up to date'}
                    </Button>
                    {participantDirty && (
                      <p className="text-[10px] text-amber-600 px-1 font-medium">Unsaved changes — click Push to apply</p>
                    )}
                    {/* Preview toggle */}
                    <label className="flex items-center gap-2 px-1 py-1.5 cursor-pointer rounded-md hover:bg-muted/50 transition-colors" data-testid="preview-as-participant-toggle">
                      <Checkbox checked={previewAsParticipant} onCheckedChange={() => setPreviewAsParticipant(v => !v)} />
                      <span className="text-xs font-medium">Preview as Participant</span>
                    </label>
                    {previewAsParticipant && (
                      <p className="text-[10px] text-amber-600 px-1 font-medium">Showing map as participants would see it</p>
                    )}
                  </div>
                </div>
              </ScrollArea>
            )}
          </div>
        )}

        {/* Map container */}
        <div className={`relative overflow-hidden border flex-1 ${canEdit ? 'rounded-r-lg' : 'rounded-lg'} ${isFullscreen ? '' : ''}`}
          style={!isFullscreen ? { height: '520px', zIndex: 0 } : { zIndex: 0 }}>

          {/* Preview as participant banner */}
          {previewAsParticipant && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[2000] bg-amber-500/90 text-white px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-2 shadow-lg backdrop-blur-sm" data-testid="participant-preview-banner">
              <Eye className="w-3.5 h-3.5" />
              Previewing as Participant
              <button onClick={() => setPreviewAsParticipant(false)} className="ml-1 hover:bg-white/20 rounded-full p-0.5"><X className="w-3 h-3" /></button>
            </div>
          )}

          <MapContainer center={center} zoom={zoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
            <MapCapture mapRef={mapRef} />
            <CoordTracker onCoords={setCoords} />
            <ScaleBar />
            <LocateMe />
            <RightClickCoords />
            <MiniMap visible={showMini} />
            <BriefingModeOverlay active={briefingMode} />

            {/* Tile layer controls - admin/simcell only */}
            {canEdit ? (
              <LayersControl position="topleft">
                {TILE_LAYERS.map((t, i) => (
                  <LayersControl.BaseLayer key={t.name} name={t.name} checked={i === 0}>
                    <TileLayer url={t.url} attribution={t.attr} />
                  </LayersControl.BaseLayer>
                ))}
                <LayersControl.Overlay checked name="OpenSeaMap (nautical)">
                  <TileLayer url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png" attribution="© OpenSeaMap" opacity={0.8} />
                </LayersControl.Overlay>
              </LayersControl>
            ) : (
              <>
                <TileLayer url={TILE_LAYERS[0].url} attribution={TILE_LAYERS[0].attr} />
                <TileLayer url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png" attribution="© OpenSeaMap" opacity={0.8} />
              </>
            )}

            <DrawControl
              canEdit={canEdit && !previewAsParticipant}
              geojson={mapData?.geojson}
              colorRef={colorRef}
              arrowModeRef={arrowModeRef}
              iconRef={iconRef}
              onCreated={handleCreated}
              onChanged={handleChanged}
              onFeatureClick={setSelectedFeature}
              onFeatureDeleted={handleFeatureDeleted}
              hiddenLayers={effectiveHiddenLayers}
              hiddenFeatures={effectiveHiddenFeatures}
              briefingMode={briefingMode}
            />
          </MapContainer>

          {/* V14.6 — "Saved" indicator + last-saver name (bottom-left overlay)
              V14.10.3 — Only super_admin sees this; others don't see who edited */}
          {mapData?.updated_at && isSuperAdmin && (
            <SavedIndicator
              updatedAt={mapData.updated_at}
              updatedByName={mapData.updated_by_name}
              saving={saving}
              onClear={async () => {
                try {
                  await exerciseMapApi.clearChangeIndicator(exerciseId);
                  setMapData((m) => m ? { ...m, updated_at: undefined, updated_by_name: undefined } : m);
                  toast.success('Change indicator cleared');
                } catch (e) {
                  toast.error(e?.message || 'Failed to clear');
                }
              }}
            />
          )}

          {/* Label modal */}
          {pendingLayer && (
            <div className="absolute inset-0 bg-black/50 z-[2000] flex items-center justify-center">
              <div className="bg-popover border rounded-xl shadow-2xl p-5 w-80 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm">Add label to shape</p>
                  <button onClick={skipLabel} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
                </div>
                <Input autoFocus placeholder="Label (e.g. Evacuation Zone A)" value={labelTxt} onChange={e => setLabelTxt(e.target.value)} className="h-8 text-sm"
                  onKeyDown={e => { if (e.key === 'Enter') confirmLabel(); if (e.key === 'Escape') skipLabel(); }} />
                <textarea placeholder="Description / notes (optional)" value={descTxt} onChange={e => setDescTxt(e.target.value)}
                  className="w-full h-16 text-sm px-3 py-2 rounded-md border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring" />
                {/* Folder selector */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground flex items-center gap-1"><FolderTree className="w-3 h-3" /> Save to folder</label>
                  <select value={targetFolder} onChange={e => setTargetFolder(e.target.value)}
                    className="w-full h-8 text-sm px-2 rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="label-folder-select">
                    <option value="Drawings">Drawings (default)</option>
                    {folders.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1 h-8 text-sm" onClick={confirmLabel}><Check className="w-3.5 h-3.5 mr-1.5" /> Save label</Button>
                  <Button variant="outline" className="h-8 text-sm" onClick={skipLabel}>Skip</Button>
                </div>
                <p className="text-xs text-muted-foreground text-center">Enter to save / Escape to skip</p>
              </div>
            </div>
          )}

          {/* Feature Details Card */}
          {selectedFeature && (
            <div className="absolute top-3 right-3 z-[2000] bg-popover/95 backdrop-blur border rounded-xl shadow-2xl p-4 w-72 space-y-2" data-testid="map-feature-details">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-sm flex items-center gap-2">
                  {selectedFeature.iconType && ALL_ICONS[selectedFeature.iconType] && (
                    <span dangerouslySetInnerHTML={{ __html: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">${ALL_ICONS[selectedFeature.iconType].svg(selectedFeature.color || '#3b82f6')}</svg>` }} />
                  )}
                  {selectedFeature.label || 'Map Feature'}
                </p>
                <button onClick={() => setSelectedFeature(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
              </div>
              {selectedFeature.iconType && ALL_ICONS[selectedFeature.iconType] && (
                <div className="text-xs text-muted-foreground">Type: {ALL_ICONS[selectedFeature.iconType].label}</div>
              )}
              {selectedFeature.layer && <div className="text-xs text-muted-foreground">Layer: {selectedFeature.layer}</div>}
              {selectedFeature.color && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  Color: <span className="w-3 h-3 rounded-full inline-block" style={{ background: selectedFeature.color }} />
                  {PALETTE.find(p => p.hex === selectedFeature.color)?.name || selectedFeature.color}
                </div>
              )}
              {selectedFeature.description && (
                <div
                  className="text-sm mt-1 pt-2 border-t kml-desc kml-desc-panel"
                  // V14.10.6 — KML descriptions can contain arbitrary HTML imported
                  // from user files. Sanitise to strip <script>, on* handlers, etc.
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedFeature.description, {
                    ALLOWED_TAGS: ['a','b','i','em','strong','p','br','span','div','table','tr','td','th','tbody','thead','ul','ol','li','img','h1','h2','h3','h4','h5','h6','hr','blockquote','code','pre'],
                    ALLOWED_ATTR: ['href','src','alt','title','target','rel','class','style','width','height'],
                    ALLOW_DATA_ATTR: false,
                  }) }}
                />
              )}
              {selectedFeature.type === 'arrow' && <Badge variant="secondary" className="text-xs">Arrow</Badge>}
              {selectedFeature.radius && <div className="text-xs text-muted-foreground">Radius: {formatDist(selectedFeature.radius)}</div>}
            </div>
          )}
        </div>
      </div>

      {/* ── Status bar ── */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground px-1">
        <div className="flex items-center gap-4 font-mono">
          {coords
            ? <span>Lat {coords.lat.toFixed(5)}, Lng {coords.lng.toFixed(5)}</span>
            : <span className="opacity-40">Hover map for coordinates</span>}
          {measurement && (
            <span className="flex items-center gap-1 text-primary font-sans">
              <Ruler className="w-3 h-3" /> {measurement}
            </span>
          )}
          {saving && <span className="animate-pulse font-sans">Saving...</span>}
        </div>
        <div className="flex items-center gap-2">
          {canEdit ? (
            <>
              <span>Draw tools top-right / Right-click for coords</span>
              {arrowMode && <Badge variant="secondary" className="text-xs h-5">Arrow mode</Badge>}
            </>
          ) : (
            <Badge variant="outline" className="text-xs h-5">View only</Badge>
          )}
        </div>

      </div>
    </div>
  );
}


// V14.6 — "Saved" indicator + last-saver name shown at bottom-left of the map
function SavedIndicator({ updatedAt, updatedByName, saving, onClear }) {
  const [, setTick] = useState(0);
  // Re-render every 15s so the relative-time label stays current
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, []);

  const rel = formatRelativeTime(updatedAt);
  return (
    <div
      className="absolute bottom-3 left-3 z-[1000] bg-card/95 backdrop-blur-sm border border-border rounded-lg shadow-md px-3 py-1.5 text-xs flex items-center gap-2 max-w-[320px]"
      data-testid="map-saved-indicator"
    >
      {saving ? (
        <>
          <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
          <span className="text-amber-700 dark:text-amber-400 font-medium">Saving…</span>
        </>
      ) : (
        <>
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
          <span className="text-emerald-700 dark:text-emerald-400 font-medium">Saved</span>
          {(updatedByName || rel) && (
            <span className="text-muted-foreground truncate">
              · {updatedByName ? `last edit by ${updatedByName}` : 'last edit'}
              {rel ? ` ${rel}` : ''}
            </span>
          )}
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              className="ml-1 px-1.5 py-0.5 rounded text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/60 border border-border shrink-0"
              data-testid="map-clear-change-indicator"
              title="Dismiss this notice for everyone"
            >
              Clear
            </button>
          )}
        </>
      )}
    </div>
  );
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
